import type { PlotData, SelectionContext } from "@/types/plot";
import { detectExclusion, type ScoreExclusion } from "./scoreExclusions";

// ── Types ──

export interface ScoreWeights {
  valuation: number;
  building: number;
  location: number;
  neighborhood: number;
  investment: number;
}

export interface SubScore {
  score: number;       // 0-100
  confidence: number;  // 0-1 (weight + reliability aware)
  factors: {
    name: string;
    score: number;
    available: boolean;
    weight: number;
    /** 0-1 trustworthiness of the underlying data; defaults to 1 (a direct measurement). */
    reliability?: number;
  }[];
}

/**
 * Non-capping risk advisory. Surfaced as an amber badge — the score is not
 * clamped, but the user is told there's an indicative risk worth investigating.
 *
 * Used for zone-level signals where the underlying dataset can't see whether
 * THIS plot has been remediated (e.g. foundation risk based on a PC6-level
 * pre-1970 building share, where individual foundations may have had
 * funderingsherstel done decades ago).
 */
export interface RiskFlag {
  /** Short headline shown next to the amber badge. */
  reason: string;
  /** One-sentence rationale shown in popovers / dossier. */
  detail: string;
}

export interface PropertyScore {
  /** Final headline score (0-100), capped if any deal-breaker risk applies. */
  composite: number;
  /** Pre-cap composite. Only present when a cap was actually applied. */
  rawComposite?: number;
  /** Description of the cap that was applied, if any. */
  cap?: { reason: string; value: number };
  /**
   * Non-capping risk advisories. Surfaced as an amber badge. Unlike `cap`,
   * these do NOT clamp the composite — they're a "you should know about this"
   * signal for risks where the per-plot evidence is too indirect to justify
   * mechanical score suppression (e.g. zone-level foundation-risk indicators
   * that can't see whether this specific foundation has been replaced).
   */
  flags?: RiskFlag[];
  /**
   * Non-null when the plot is flagged by `detectExclusion` (memorial, park,
   * school, heritage, etc.). The UI must branch on `exclusion.tier`:
   *   - "not_scored"   → hide composite AND sub-scores
   *   - "context_only" → hide composite, keep sub-scores
   *   - "caveat"       → show composite with a prominent badge
   * `composite` and `sub` remain populated so callers that want to show
   * internal-only diagnostics or export raw signals still can.
   */
  exclusion?: ScoreExclusion;
  confidence: number;
  sub: {
    valuation: SubScore;
    building: SubScore;
    location: SubScore;
    neighborhood: SubScore;
    investment: SubScore;
  };
}

// ── Presets ──

export const PRESETS: Record<string, ScoreWeights> = {
  // Balanced — no axis dominates; default for casual users.
  balanced:  { valuation: 25, building: 15, location: 25, neighborhood: 20, investment: 15 },

  // Investor — financial upside dominates, location matters because of tenant demand.
  investor:  { valuation: 15, building: 10, location: 20, neighborhood: 15, investment: 40 },

  // Developer — building's developable envelope is the #1 question, then upside.
  // (Building factor weights inside scoreBuilding already capture buildable envelope + headrooms.)
  developer: { valuation: 15, building: 35, location: 15, neighborhood: 10, investment: 25 },

  // Homeowner — you live in the building, on the location, in the neighborhood.
  // Investment matters less day-to-day but is still relevant for long-term wealth.
  homeowner: { valuation: 20, building: 25, location: 25, neighborhood: 20, investment: 10 },
};

// ── Asset-class-aware weighting ──
//
// PlotScore scores residential, commercial, mixed-use AND vacant/developable
// land. A single lens can't weight all four coherently, so the layer weights
// are resolved from the OBJECTIVE asset class of what's being scored — never a
// user choice, so scores stay comparable within a class. The class follows the
// scope: a mixed-use building scored whole is an area-weighted blend; the same
// building's 3rd-floor apartment scored as a unit is pure residential.
//
// The five ScoreWeights keys map to the published layer names:
//   valuation → Value & Tenure        building → Object
//   location → Location & Environment  neighborhood → Neighbourhood
//   investment → Risk & Outlook
//
// Weights are research-calibrated (see PLOTSCORE_SCORING_REFERENCE.md): intrinsic
// "Object" attributes (size/energy/type) dominate residential value; commercial
// value is location- and income-led; vacant land has no building, so Object's
// weight folds into development optionality + risk.

export type AssetClass = "residential" | "commercial" | "mixed" | "land";

// Exported so out-of-app consumers (e.g. the offline Atlas scorer in
// scripts/build-atlas-scores.ts) resolve the SAME weights as the live app
// instead of hand-copying the table — a copy silently drifts on the next
// methodology change and makes the map show a different score than the sidebar.
export const CLASS_WEIGHTS: Record<Exclude<AssetClass, "mixed">, ScoreWeights> = {
  residential: { valuation: 15, building: 30, location: 30, neighborhood: 12, investment: 13 },
  commercial:  { valuation: 25, building: 25, location: 30, neighborhood: 8,  investment: 12 },
  land:        { valuation: 20, building: 0,  location: 30, neighborhood: 10, investment: 40 },
};

function gebruiksdoelIsResidential(g: string | undefined): boolean {
  return (g ?? "").toLowerCase().includes("woonfunctie");
}

/**
 * Resolve the objective asset class for the scored scope.
 *  - Unit scope → the selected unit's BAG gebruiksdoel alone (so an apartment
 *    inside a mixed-use block is scored residential).
 *  - Building scope → floor-area-weighted residential share across all units.
 *  - No addresses → land (a drawn / vacant parcel).
 */
export function resolveAssetClass(
  plot: PlotData,
  ctx?: SelectionContext,
): { assetClass: AssetClass; residentialShare: number } {
  const addrs = plot.addresses ?? [];

  if (ctx?.scope === "unit" && ctx.selectedAddressIndex != null) {
    const a = addrs[ctx.selectedAddressIndex];
    if (a?.gebruiksdoel) {
      const res = gebruiksdoelIsResidential(a.gebruiksdoel);
      return { assetClass: res ? "residential" : "commercial", residentialShare: res ? 1 : 0 };
    }
  }

  if (addrs.length === 0) {
    return { assetClass: "land", residentialShare: 0 };
  }

  // Floor-area-weighted residential share; fall back to unit-count weighting
  // when a VBO carries no oppervlakte.
  let resArea = 0;
  let totalArea = 0;
  for (const a of addrs) {
    const w = a.oppervlakte != null && a.oppervlakte > 0 ? a.oppervlakte : 1;
    totalArea += w;
    if (gebruiksdoelIsResidential(a.gebruiksdoel)) resArea += w;
  }
  const share = totalArea > 0 ? resArea / totalArea : 0;
  if (share >= 0.85) return { assetClass: "residential", residentialShare: 1 };
  if (share <= 0.15) return { assetClass: "commercial", residentialShare: 0 };
  return { assetClass: "mixed", residentialShare: share };
}

/**
 * Class-resolved layer weights for a plot/scope. Mixed-use blends the
 * residential and commercial lenses by residential floor-area share.
 * computePropertyScore normalises to sum 100, so fractional weights are fine.
 */
export function weightsForScope(plot: PlotData, ctx?: SelectionContext): ScoreWeights {
  const { assetClass, residentialShare: s } = resolveAssetClass(plot, ctx);
  if (assetClass !== "mixed") return CLASS_WEIGHTS[assetClass];
  const r = CLASS_WEIGHTS.residential;
  const c = CLASS_WEIGHTS.commercial;
  return {
    valuation:    r.valuation * s + c.valuation * (1 - s),
    building:     r.building * s + c.building * (1 - s),
    location:     r.location * s + c.location * (1 - s),
    neighborhood: r.neighborhood * s + c.neighborhood * (1 - s),
    investment:   r.investment * s + c.investment * (1 - s),
  };
}

/**
 * True when the plot has been projected down to ONE unit of a building that
 * holds several.
 *
 * `projectUnit` rewrites `woz` for the unit it describes but copies the
 * BUILDING's fields through — the parcel area, the valuation history, the
 * other dwellings. Anything that belongs to the building rather than to the
 * buyer has to check this, or it scores an apartment on what its block owns.
 * Both directions of that error have shipped: Multi-unit read the synthesised
 * `unitCount: 1` and penalised every flat in the country, and Plot Size read
 * the whole parcel and credited them with it.
 *
 * `buildingUnitCount` is the only surviving evidence, which is why the
 * projection carries it.
 */
function isUnitOfMultiUnitBuilding(plot: PlotData): boolean {
  const w = plot.woz;
  return w?.unitCount === 1 && (w?.buildingUnitCount ?? 1) > 1;
}

// ── Shared CAGR helpers (extracted from ValuationCard) ──

export function computeCAGR(historie: { peildatum: string; vastgesteldeWaarde: number }[]): number | null {
  if (!historie || historie.length < 2) return null;
  const sorted = [...historie].sort((a, b) => a.peildatum.localeCompare(b.peildatum));
  const oldest = sorted[0];
  const latest = sorted[sorted.length - 1];
  const years = parseInt(latest.peildatum.slice(0, 4)) - parseInt(oldest.peildatum.slice(0, 4));
  if (years <= 0 || oldest.vastgesteldeWaarde <= 0) return null;
  return (Math.pow(latest.vastgesteldeWaarde / oldest.vastgesteldeWaarde, 1 / years) - 1) * 100;
}

export function computeMarketCAGR(history: { year: string; pricePerSqm: number }[]): number | null {
  if (!history || history.length < 2) return null;
  const sorted = [...history].sort((a, b) => a.year.localeCompare(b.year));
  const oldest = sorted[0];
  const latest = sorted[sorted.length - 1];
  const years = parseInt(latest.year) - parseInt(oldest.year);
  if (years <= 0 || oldest.pricePerSqm <= 0) return null;
  return (Math.pow(latest.pricePerSqm / oldest.pricePerSqm, 1 / years) - 1) * 100;
}

// ── Factor scoring helpers ──

/**
 * Linear interpolation between piecewise (value, score) anchors.
 * Anchors must be sorted by ascending value (the score may go up OR down).
 * Values outside the range clamp to the nearest anchor's score.
 */
function lerp(value: number, anchors: [number, number][]): number {
  if (anchors.length === 0) return 0;
  if (value <= anchors[0][0]) return anchors[0][1];
  if (value >= anchors[anchors.length - 1][0]) return anchors[anchors.length - 1][1];
  for (let i = 0; i < anchors.length - 1; i++) {
    const [x0, y0] = anchors[i];
    const [x1, y1] = anchors[i + 1];
    if (value >= x0 && value <= x1) {
      const frac = x1 === x0 ? 0 : (value - x0) / (x1 - x0);
      return Math.round(y0 + frac * (y1 - y0));
    }
  }
  return anchors[anchors.length - 1][1];
}

/** Map a value to 0-100 given ascending thresholds. Higher value = higher score. Smooth (linear interp). */
function scoreAsc(value: number, thresholds: [number, number][]): number {
  return lerp(value, thresholds);
}

/** Map a value to 0-100 given descending thresholds. Lower value = higher score (e.g., distance). Smooth (linear interp). */
function scoreDesc(value: number, thresholds: [number, number][]): number {
  return lerp(value, thresholds);
}

/**
 * Where a value sits in a measured distribution, as a percentile 0-100.
 *
 * `breakpoints` is the bake's own percentile map ("p5" -> -2.8, ...). Between
 * two breakpoints it interpolates; outside the outermost ones it clamps,
 * because the bake does not describe the tails beyond what it measured and
 * extrapolating would be inventing distribution it never saw.
 */
function nationalPercentile(value: number, breakpoints: Record<string, number>): number {
  const points = Object.entries(breakpoints)
    .map(([k, v]) => [parseFloat(k.slice(1)), v] as [number, number])
    .filter(([q, v]) => Number.isFinite(q) && Number.isFinite(v))
    // By percentile, which for a sinking-is-negative field also sorts by value.
    .sort((a, b) => a[0] - b[0]);
  if (!points.length) return 50;
  if (value <= points[0][1]) return points[0][0];
  for (let i = 1; i < points.length; i++) {
    const [q0, v0] = points[i - 1];
    const [q1, v1] = points[i];
    if (value <= v1) {
      const k = v1 === v0 ? 0 : (value - v0) / (v1 - v0);
      return q0 + k * (q1 - q0);
    }
  }
  return points[points.length - 1][0];
}

// ── Dutch economic centres (CBD / job hubs) ──
// Haversine distance to the nearest one is a strong location signal — not just
// "is there a station nearby?" but "does this location connect you to where
// the jobs actually are?". Coordinates are approximate centroids.
const ECONOMIC_CENTRES: { name: string; lat: number; lng: number }[] = [
  { name: "Amsterdam Zuidas",       lat: 52.3389, lng: 4.8728 },
  { name: "Amsterdam Centraal",     lat: 52.3791, lng: 4.9003 },
  { name: "Rotterdam Centraal",     lat: 51.9244, lng: 4.4693 },
  { name: "Utrecht Centraal",       lat: 52.0893, lng: 5.1101 },
  { name: "Den Haag Centraal",      lat: 52.0808, lng: 4.3250 },
  { name: "Eindhoven Centraal",     lat: 51.4433, lng: 5.4811 },
  { name: "Schiphol Airport",       lat: 52.3105, lng: 4.7683 },
  { name: "Groningen Centraal",     lat: 53.2108, lng: 6.5642 },
];

/** Haversine distance between two WGS84 points, in metres. */
function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function nearestCbdDistance(plot: PlotData): { name: string; distanceKm: number } | null {
  const c = plot.center;
  if (!c) return null;
  let best: { name: string; distanceKm: number } | null = null;
  for (const cbd of ECONOMIC_CENTRES) {
    const d = haversineM(c.lat, c.lng, cbd.lat, cbd.lng) / 1000;
    if (!best || d < best.distanceKm) best = { name: cbd.name, distanceKm: d };
  }
  return best;
}

/** Get the closest amenity distance for a given category. */
function amenityDist(plot: PlotData, key: keyof NonNullable<PlotData["amenities"]>): number | null {
  const items = plot.amenities?.[key];
  if (!items || items.length === 0) return null;
  return items[0].distance;
}

/**
 * Best authoritative usable floor area (BAG gebruiksoppervlakte, m²) for the
 * scored scope: the unit's own area at unit scope (woz.unitCount === 1, set by
 * projectScope), else the summed per-VBO areas across the building. Returns
 * null when BAG carries no area (caller falls back to the 3D BVO estimate).
 */
export function usableFloorArea(plot: PlotData): number | null {
  const woz = plot.woz;
  if (woz?.unitCount === 1 && woz.primaryUnitSqm != null && woz.primaryUnitSqm > 0) {
    return woz.primaryUnitSqm;
  }
  const sum = (plot.addresses ?? []).reduce(
    (s, a) => s + (a.oppervlakte != null && a.oppervlakte > 0 ? a.oppervlakte : 0),
    0,
  );
  if (sum > 0) return sum;
  return woz?.primaryUnitSqm != null && woz.primaryUnitSqm > 0 ? woz.primaryUnitSqm : null;
}

// ── Sub-score computations ──

/**
 * Optional per-plot calibration overrides for the size factors, resolved by
 * the caller (percentile breakpoints for THIS plot's type/peer group). When
 * absent, the curated anchors are used — so default scoring is unchanged.
 * Accuracy-roadmap #2 (percentile anchoring).
 */
export interface ScoreCalibration {
  /** Single-dwelling Usable Area breakpoints [m², score]. */
  usableDwelling?: [number, number][];
  /** Plot Size breakpoints [m², score]. */
  plotSize?: [number, number][];
}

function scoreValuation(plot: PlotData, cal?: ScoreCalibration): SubScore {
  const factors: SubScore["factors"] = [];
  const woz = plot.woz;

  // Factor 1: WOZ €/m² (Dutch avg ~€2500-4000, Amsterdam ~€5000-8000) — most direct price signal
  const perSqm = woz?.avgWozPerSqm;
  if (perSqm != null) {
    factors.push({ name: "WOZ €/m²", score: scoreAsc(perSqm, [[1500, 20], [2500, 35], [3500, 50], [4500, 65], [5500, 75], [7000, 85], [9000, 95]]), available: true, weight: 3 });
  } else {
    factors.push({ name: "WOZ €/m²", score: 0, available: false, weight: 3 });
  }

  // Factor 2: WOZ vs neighborhood (% deviation) — over/under vs comparables
  const vsHood = woz?.wozVsNeighborhood;
  if (vsHood != null) {
    // Positive deviation = property is above neighborhood avg
    factors.push({ name: "vs Neighborhood", score: scoreAsc(vsHood, [[-20, 15], [-10, 30], [-5, 40], [0, 55], [5, 65], [10, 80], [20, 95]]), available: true, weight: 2 });
  } else {
    factors.push({ name: "vs Neighborhood", score: 0, available: false, weight: 2 });
  }

  // (WOZ Growth removed — it lives in Investment as "Value Growth", same computeCAGR call)

  // Factor 3: Ground area (larger = more valuable for development)
  // BUG FIX (E1): Only use woz.grondoppervlakte (actual cadastral ground area).
  // building3d.opp_grond is the building FOOTPRINT, not the plot size — a large
  // building on a small plot would have opp_grond > actual plot area, inflating the score.
  // Fall back to plot.area (drawn polygon area) if WOZ doesn't carry ground area.
  // An apartment does not own the parcel — it owns a share of it plus its own
  // unit — so the building's ground area is not this buyer's plot size. Scored
  // as though it were, the effect was backwards as well as wrong: the MORE
  // units sharing the land, the higher each scored. Measured 2026-09-08: a flat
  // in a 40-unit block on 2000 m2 scored 95 for Plot Size, weight 1.5 in
  // Valuation, on a share of about 50 m2.
  //
  // Reported unavailable rather than divided down. `parcel / units` looks like
  // a measurement but an appartementsrecht share is not a plot you can build
  // on, extend into or sell separately, so the quotient would be a number with
  // no referent (§Marketing claims).
  const ground = isUnitOfMultiUnitBuilding(plot)
    ? null
    : woz?.grondoppervlakte || (plot.area != null && plot.area > 0 ? plot.area : null);
  if (ground != null && ground > 0) {
    factors.push({ name: "Plot Size", score: scoreAsc(ground, cal?.plotSize ?? [[50, 25], [100, 40], [200, 55], [400, 70], [800, 85], [2000, 95]]), available: true, weight: 1.5 });
  } else {
    factors.push({ name: "Plot Size", score: 0, available: false, weight: 1.5 });
  }

  // Factor 3b: Erfpacht (ground lease) — only Amsterdam currently. Massive
  // valuation impact: short-term leases trade at material discounts; perpetual
  // (eeuwigdurend) leases are essentially full ownership. The route currently
  // scaffolds this — when the data feed is wired, this factor activates automatically.
  const erf = plot.erfpacht;
  if (erf?.loaded && erf.supported && erf.available) {
    let erfScore: number;
    if (erf.leaseType === "eeuwigdurend") erfScore = 80;       // perpetual ≈ full ownership
    else if (erf.leaseType === "voortdurend") erfScore = 55;   // ongoing fixed-term
    else if (erf.leaseType === "tijdelijk") {
      // Temporary lease — score by years remaining. Use the calendar-aware
      // millisecond delta divided by a fixed year length, then floor, so the
      // score steps once per year on the lease's anniversary date rather than
      // jumping every January 1st on `getFullYear()` (which made the same plot
      // re-score differently around year boundaries).
      let yearsLeft = 0;
      if (erf.endDate) {
        const end = new Date(erf.endDate).getTime();
        if (Number.isFinite(end)) {
          const MS_PER_YEAR = 365.2425 * 24 * 3600 * 1000;
          yearsLeft = Math.max(0, Math.floor((end - Date.now()) / MS_PER_YEAR));
        }
      }
      erfScore = scoreAsc(yearsLeft, [[0, 10], [5, 20], [15, 35], [30, 50], [50, 65], [99, 75]]);
    } else {
      erfScore = 60;
    }
    factors.push({ name: "Erfpacht", score: erfScore, available: true, weight: 2, reliability: 0.9 });
  } else if (erf?.loaded && erf.supported === false) {
    // Outside Amsterdam — factor not applicable, omit silently (don't penalise non-Amsterdam plots).
  } else {
    // Inside Amsterdam but data not yet wired or fetch failed — mark unavailable so the UI can show "loading…"
    factors.push({ name: "Erfpacht", score: 0, available: false, weight: 2, reliability: 0.9 });
  }

  // Factor 4: Value for Money — the property's OWN market estimate vs its OWN
  // WOZ, both per m². This is the valid re-add of the 2026-06-removed "Price
  // vs WOZ gap" factor (which compared WOZ to the GEMEENTE-average €/m² and
  // structurally penalised premium homes): now that /api/market-estimate
  // supplies local comps for the specific address, both sides of the ratio are
  // property-specific, so a canal house is compared to its own market position
  // — not to the city mean. A small market premium over the assessed value =
  // good value-for-money; a large one = the buyer pays a premium the
  // assessment doesn't back.
  //
  // Availability caveat: the estimate is a Pass+ enrichment (the API gates
  // it), so free members score WITHOUT this factor. Deliberately OMITTED (not
  // available:false) when absent — same pattern as erfpacht outside Amsterdam
  // — so a missing estimate never drags valuation confidence. Reliability
  // follows the estimate's own confidence tier.
  const est = plot.marketEstimate;
  if (est?.available && est.perSqm != null && perSqm != null && perSqm > 0) {
    const premiumPct = (est.perSqm / perSqm - 1) * 100;
    const reliability = est.confidence === "high" ? 0.95 : est.confidence === "medium" ? 0.7 : 0.5;
    factors.push({
      name: "Value for Money",
      score: scoreDesc(premiumPct, [[-5, 92], [0, 85], [8, 72], [15, 60], [25, 45], [40, 28], [60, 12]]),
      available: true,
      weight: 2,
      reliability,
    });
  }

  return computeSubScore(factors);
}

function scoreBuilding(plot: PlotData, cal?: ScoreCalibration): SubScore {
  const factors: SubScore["factors"] = [];
  const b3d = plot.building3d;

  // Factor 1: Energy label — single biggest building signal (value + running cost)
  const label = plot.energyLabel?.buildingLabel;
  if (label) {
    const labelScores: Record<string, number> = { "A+++++": 100, "A++++": 100, "A+++": 100, "A++": 98, "A+": 95, "A": 90, "B": 75, "C": 60, "D": 45, "E": 30, "F": 18, "G": 10 };
    factors.push({ name: "Energy Label", score: labelScores[label] ?? 50, available: true, weight: 3 });
  } else {
    factors.push({ name: "Energy Label", score: 0, available: false, weight: 3 });
  }

  // Factor 1b: Actual energy use — average billed gas (m³/yr per connection) on
  // this property's own PC6, from the netbeheerder kleinverbruik data. The
  // Energy Label above is the MODELLED efficiency; this is what the street
  // ACTUALLY burns. A real running-cost signal, but the PC6 average mixes
  // residential with small-commercial connections (in mixed-use cores it runs
  // high — central-Amsterdam PC6s show ~3000-6000 m³ vs a residential ~1100)
  // and is blind to dwelling size. So it's a GENTLE, low-confidence signal:
  // modest weight + reliability 0.55, with anchors that reward efficient
  // streets without cratering commercial-mixed ones. (A residential-only
  // connection-type filter or per-m² normalisation is the v2 refinement.)
  // Inactive until energy_pc6 is ingested (scripts/build-energy-data.mjs).
  // Only participates once the energy_pc6 table is live — the route sets
  // plot.energyUse only when it responds loaded:true. Before ingest the factor
  // is omitted entirely, so it can't dilute Building confidence prematurely.
  const energyUse = plot.energyUse;
  if (energyUse?.loaded) {
    if (energyUse.found && energyUse.gasM3 != null) {
      factors.push({
        name: "Energy Use",
        score: scoreDesc(energyUse.gasM3, [[700, 90], [1100, 70], [1500, 55], [2200, 40], [4000, 25]]),
        available: true,
        weight: 0.75,
        reliability: 0.55,
      });
    } else {
      factors.push({ name: "Energy Use", score: 0, available: false, weight: 0.75, reliability: 0.55 });
    }
  }

  // Factor 2: Building floors — descriptive, not quality
  // BUG FIX (E3): Extended anchors so a 20-story tower scores higher than 10 floors.
  // Most Dutch residential is 2-4 floors; 10+ is exceptional; 20+ is high-rise.
  const floors = b3d?.bouwlagen;
  if (floors != null) {
    factors.push({ name: "Floors", score: scoreAsc(floors, [[1, 25], [2, 45], [3, 55], [4, 65], [6, 75], [10, 85], [20, 95]]), available: true, weight: 1 });
  } else {
    factors.push({ name: "Floors", score: 0, available: false, weight: 1 });
  }

  // Factor 3: Usable floor area — the single strongest value driver (NL hedonic
  // evidence). Prefer the authoritative BAG gebruiksoppervlakte (per-VBO; summed
  // across units at building scope, the unit's own area at unit scope) over the
  // 3D gross BVO estimate, which over/understates true usable area. `gfa` is
  // kept for the volume-efficiency / buildable-envelope factors below.
  const gfa = b3d?.geschat_bvo;
  const authUsable = usableFloorArea(plot);
  const usable = authUsable ?? (gfa != null && gfa > 0 ? gfa : null);
  if (usable != null && usable > 0) {
    // Scope-aware anchors. A single DWELLING (a unit, or a one-dwelling
    // building — a 70 m² flat and a 70 m² row house are the same size class)
    // is scored against the NL dwelling-size distribution; a multi-unit
    // AGGREGATE (summed per-VBO areas at building scope) against the old
    // building curve. Before this split, every unit was scored on the
    // building curve — a 68 m² apartment could never rise above ~31/100 on a
    // weight-3 factor, structurally dragging unit scores below their own
    // building's.
    // Address count first: projectScope narrows `addresses` to the single
    // unit at unit scope, while `woz.unitCount` can still be the building's
    // (units without their own WOZ row inherit the building WOZ).
    const isAggregate = ((plot.addresses?.length || plot.woz?.unitCount) ?? 1) > 1;
    const anchors: [number, number][] = isAggregate
      ? [[40, 20], [75, 35], [110, 50], [160, 62], [250, 75], [500, 85], [1500, 95]]
      : (cal?.usableDwelling ?? [[30, 20], [50, 35], [70, 48], [95, 60], [120, 70], [160, 82], [220, 90], [300, 95]]);
    factors.push({
      name: "Usable Area",
      score: scoreAsc(usable, anchors),
      available: true,
      weight: 3,
      reliability: authUsable != null ? 1 : 0.7, // authoritative BAG area vs 3D estimate
    });
  } else {
    factors.push({ name: "Usable Area", score: 0, available: false, weight: 3 });
  }

  // Factor 4: Wall exposure ratio — detached = NL premium
  const gevelRatio = b3d?.gevel_ratio;
  if (gevelRatio != null) {
    factors.push({ name: "Detachment", score: scoreAsc(gevelRatio, [[0.2, 25], [0.4, 40], [0.6, 60], [0.75, 75], [0.9, 90]]), available: true, weight: 1.5 });
  } else {
    factors.push({ name: "Detachment", score: 0, available: false, weight: 1.5 });
  }

  // (Energy Efficiency removed — it was a NEIGHBORHOOD avgGasM3 proxy. The building-level
  //  energy story is already covered by Energy Label, and the neighborhood gas % lives in
  //  Investment as "Heat Transition".)

  // Factor 5: Height headroom — developer-critical
  const zd = plot.zoningDetail;
  const maxH = zd?.maxBouwhoogte;
  const actH = plot.building3d?.hoogte;
  if (maxH != null && maxH > 0 && actH != null) {
    const headroom = Math.max(0, (maxH - actH) / maxH);
    factors.push({ name: "Height Headroom", score: scoreAsc(headroom, [[0, 20], [0.1, 35], [0.3, 50], [0.5, 65], [0.7, 80], [1.0, 90]]), available: true, weight: 2 });
  } else {
    factors.push({ name: "Height Headroom", score: 0, available: false, weight: 2 });
  }

  // Factor 7: Coverage headroom — developer-critical
  const maxCov = zd?.maxBebouwingspercentage;
  if (maxCov != null && maxCov > 0 && plot.building3d?.opp_grond != null && plot.area != null && plot.area > 0) {
    const actualCovPct = (plot.building3d.opp_grond / plot.area) * 100;
    const covHeadroom = Math.max(0, (maxCov - actualCovPct) / maxCov);
    factors.push({ name: "Coverage Headroom", score: scoreAsc(covHeadroom, [[0, 20], [0.1, 35], [0.3, 50], [0.5, 65], [0.7, 80], [1.0, 90]]), available: true, weight: 2 });
  } else {
    factors.push({ name: "Coverage Headroom", score: 0, available: false, weight: 2 });
  }

  // Factor 8: Solar potential — prefer PVGIS measured yield (kWh/kWp/yr).
  //
  // PVGIS combines 19 years of SARAH-3 satellite irradiance with ERA5 reanalysis
  // and a DEM-calculated horizon, so it captures BOTH (a) latitude/climate and
  // (b) the actual roof orientation+pitch we feed it. The anchors below are
  // calibrated to NL-typical yields:
  //   ~650 kWh/kWp → north-facing steep slope (poor)
  //   ~850         → east/west sloped
  //   ~950         → south 35° (good)
  //   ~1020        → optimal-angle rack (NL best-case)
  // Fallback to the old azimut+pitch heuristic keeps the factor scoring plots
  // whose PVGIS call failed or returned before the 3DBAG data was available.
  const sp = plot.solarPotential;
  const dakType = b3d?.dak_type;
  const azimut = b3d?.azimut;
  const pitch = b3d?.hellingshoek;
  if (sp?.loaded && sp.yieldKwhPerKwp != null) {
    factors.push({
      name: "Solar Potential",
      score: scoreAsc(sp.yieldKwhPerKwp, [[600, 10], [750, 25], [850, 45], [920, 65], [970, 80], [1020, 92], [1080, 98]]),
      available: true,
      weight: 1,
      reliability: 0.9, // PVGIS validation: ~5% RMSE vs measured installations
    });
  } else if (dakType != null) {
    // Fallback heuristic (legacy): roof type + orientation + pitch.
    let solarScore = 50;
    if (dakType.toLowerCase().includes("plat")) {
      solarScore = 85;
    } else if (azimut != null && pitch != null) {
      const isSouthFacing = azimut >= 135 && azimut <= 225;
      const isEastWest = (azimut >= 90 && azimut < 135) || (azimut > 225 && azimut <= 270);
      if (isSouthFacing && pitch <= 45) solarScore = 90;
      else if (isSouthFacing) solarScore = 75;
      else if (isEastWest && pitch <= 35) solarScore = 65;
      else solarScore = 35;
    }
    // Lower reliability on the heuristic so "actual yield = lower" doesn't get
    // masked by a confident-but-wrong orientation-only estimate.
    factors.push({ name: "Solar Potential", score: solarScore, available: true, weight: 1, reliability: 0.6 });
  } else {
    factors.push({ name: "Solar Potential", score: 0, available: false, weight: 1 });
  }

  // Factor 9: Volume efficiency (volume / GFA — ~3.0 normal for 3m ceilings) — niche proxy
  const volume = b3d?.volume;
  if (volume != null && gfa != null && gfa > 0) {
    const ratio = volume / gfa;
    factors.push({ name: "Volume Efficiency", score: scoreAsc(ratio, [[2.5, 30], [2.8, 45], [3.0, 60], [3.3, 70], [3.6, 80], [4.0, 85]]), available: true, weight: 0.5 });
  } else {
    factors.push({ name: "Volume Efficiency", score: 0, available: false, weight: 0.5 });
  }

  // Factor 9: Area solar adoption — NEIGHBORHOOD proxy (down-weighted + lower reliability)
  const solarPct = plot.neighborhood?.solarPanelPct;
  if (solarPct != null) {
    factors.push({ name: "Solar Adoption", score: scoreAsc(solarPct, [[5, 25], [10, 35], [20, 50], [30, 65], [40, 80], [50, 90]]), available: true, weight: 0.5, reliability: 0.6 });
  } else {
    factors.push({ name: "Solar Adoption", score: 0, available: false, weight: 0.5, reliability: 0.6 });
  }

  // Factor 10: Construction year (BAG oorspronkelijkBouwjaar)
  // Buyers care about this for renovation needs; bell-curve-ish:
  // pre-1900 = monument-grade old, 1900-1945 = pre-war (often charm but high maintenance),
  // 1946-1970 = post-war (often poor insulation), 1971-2000 = modern, 2000+ = new (best)
  const bouwjaar = plot.buildings?.find(b => b?.bouwjaar != null)?.bouwjaar;
  if (bouwjaar != null && bouwjaar > 1700 && bouwjaar <= new Date().getFullYear() + 1) {
    // Score curve: 1900→55, 1945→35, 1970→40, 1990→60, 2010→80, 2020→90, 2025→95
    factors.push({
      name: "Construction Year",
      score: scoreAsc(bouwjaar, [[1850, 50], [1900, 55], [1945, 35], [1970, 40], [1990, 60], [2010, 80], [2020, 90], [2025, 95]]),
      available: true,
      weight: 1.5,
    });
  } else {
    factors.push({ name: "Construction Year", score: 0, available: false, weight: 1.5 });
  }

  // Factor 11: Buildable envelope — total developable GFA still legally available.
  // (currentGfa is `geschat_bvo`; max possible GFA = plotArea × maxCoverage% × maxFloors-equivalent)
  // We approximate maxFloors from maxBouwhoogte / 3m floor-height assumption.
  const maxCovPct = zd?.maxBebouwingspercentage;
  const maxBouwhoogte = zd?.maxBouwhoogte;
  if (
    maxCovPct != null && maxCovPct > 0 &&
    maxBouwhoogte != null && maxBouwhoogte > 0 &&
    plot.area != null && plot.area > 0
  ) {
    const maxFloorsApprox = Math.max(1, Math.floor(maxBouwhoogte / 3));
    const maxGfa = (plot.area * (maxCovPct / 100)) * maxFloorsApprox;
    const currentGfa = b3d?.geschat_bvo ?? 0;
    const developableGfa = Math.max(0, maxGfa - currentGfa);
    // Score by absolute developable m² — even 100m² extra is meaningful in dense areas
    factors.push({
      name: "Buildable Envelope",
      score: scoreAsc(developableGfa, [[0, 15], [50, 30], [150, 45], [400, 60], [1000, 75], [3000, 90]]),
      available: true,
      weight: 1.5,
      reliability: 0.75, // approximation: 3m floor assumption + treats all height as buildable
    });
  } else {
    factors.push({ name: "Buildable Envelope", score: 0, available: false, weight: 1.5, reliability: 0.75 });
  }

  return computeSubScore(factors);
}

function scoreLocation(plot: PlotData): SubScore {
  const factors: SubScore["factors"] = [];

  // Factor 1: Accessibility score (0-100, pre-computed composite) — broad transport signal
  const accScore = plot.infrastructure?.accessibilityScore;
  if (accScore != null) {
    factors.push({ name: "Transport Access", score: accScore, available: true, weight: 2 });
  } else {
    factors.push({ name: "Transport Access", score: 0, available: false, weight: 2 });
  }

  // Factor 1a: Transit stop density (PDOK Haltes — bus/tram/metro within 1km)
  // Complements train-station proximity with local public-transport coverage.
  const transit = plot.transit;
  if (transit?.loaded && transit.totalTransitStops != null) {
    const busNearest = transit.busStops?.nearest;
    const tramNearest = transit.tramStops?.nearest;
    const metroNearest = transit.metroStops?.nearest;
    // Score by total stop count + bonus for tram/metro presence
    let transitScore = scoreAsc(transit.totalTransitStops, [[0, 15], [3, 30], [8, 50], [15, 65], [25, 80], [40, 92]]);
    // Tram/metro within 500m is a significant urban transit premium
    if ((tramNearest != null && tramNearest < 500) || (metroNearest != null && metroNearest < 500)) {
      transitScore = Math.min(100, transitScore + 8);
    }
    factors.push({ name: "Transit Stops", score: transitScore, available: true, weight: 1.5 });
  } else {
    factors.push({ name: "Transit Stops", score: 0, available: false, weight: 1.5 });
  }

  // Factor 1b: Train station proximity — the single biggest NL location lever.
  // Prefer authoritative NS data (from /api/ns-stations) when present, fall back to
  // the legacy regex-based `nearestStation.size`.
  const nsNearest = plot.infrastructure?.nsNearest;
  const nsNearestIc = plot.infrastructure?.nsNearestIc;
  const legacyStation = plot.infrastructure?.nearestStation;
  // Distance to nearest of any type
  const stationDist = nsNearest?.distance ?? legacyStation?.distance ?? null;
  if (stationDist != null) {
    let stationScore = scoreDesc(stationDist, [[300, 100], [600, 90], [1000, 75], [1500, 60], [2500, 40], [4000, 20]]);
    // IC bonus — prefer authoritative NS classification, fall back to legacy regex match
    const isIntercity = nsNearest?.isIntercity === true
      || (legacyStation?.size != null && /intercity|ic/i.test(legacyStation.size));
    if (isIntercity) stationScore = Math.min(100, stationScore + 5);
    factors.push({ name: "Train Station", score: stationScore, available: true, weight: 2.5 });
  } else {
    factors.push({ name: "Train Station", score: 0, available: false, weight: 2.5 });
  }

  // Factor 1c: Distance to nearest IC station — separate from "any station". Many plots
  // are near a Sprinter stop but far from an IC, which is a meaningful gap for commuters.
  // Only adds value when NS data is present AND nearestIc is different from nearest.
  if (nsNearestIc?.distance != null && nsNearest?.distance != null
      && nsNearestIc.distance > nsNearest.distance + 200) {
    const icScore = scoreDesc(nsNearestIc.distance, [[500, 100], [1500, 85], [3000, 65], [6000, 45], [12000, 25], [25000, 10]]);
    factors.push({ name: "IC Station", score: icScore, available: true, weight: 1.5, reliability: 0.9 });
  }
  // (When the nearest station IS already IC, we don't add a redundant second factor.)

  // Factor 2: Nearest supermarket — daily-life essential
  const superDist = amenityDist(plot, "supermarkets");
  if (superDist != null) {
    factors.push({ name: "Supermarket", score: scoreDesc(superDist, [[300, 100], [500, 80], [800, 60], [1200, 40], [2000, 20]]), available: true, weight: 2 });
  } else {
    factors.push({ name: "Supermarket", score: 0, available: false, weight: 2 });
  }

  // Factor 3a: School Access — distance to nearest primary school.
  //
  // Prefers the distance to the nearest Goed/Voldoende/Basistoezicht school
  // (the DUO+Inspectie join), because a nearby Onvoldoende school does not
  // actually serve the family-catchment need. Falls back progressively:
  //   1. nearestGood from schoolsNearby  (quality-validated)
  //   2. nearest from schoolsNearby      (DUO address, no verdict)
  //   3. OSM amenity distance            (legacy, used when build data missing)
  const sn = plot.schoolsNearby;
  const snGoodDist = sn?.loaded ? sn.nearestGood?.distance : undefined;
  const snAnyDist  = sn?.loaded ? sn.nearest?.distance    : undefined;
  const osmDist = amenityDist(plot, "schools");

  if (snGoodDist != null) {
    factors.push({
      name: "School",
      score: scoreDesc(snGoodDist, [[500, 100], [800, 85], [1200, 65], [2000, 45], [3500, 25], [5000, 15]]),
      available: true,
      weight: 1.5,
      reliability: 0.9, // DUO authoritative address + quality filter
    });
  } else if (snAnyDist != null) {
    // We have DUO-mapped schools nearby but none with a verdict — score as
    // proximity-only but slightly lower reliability (can't rule out a poor
    // school).
    factors.push({
      name: "School",
      score: scoreDesc(snAnyDist, [[500, 95], [800, 80], [1200, 60], [2000, 40], [3000, 20]]),
      available: true,
      weight: 1.5,
      reliability: 0.8,
    });
  } else if (osmDist != null) {
    // OSM fallback — what the old factor did.
    factors.push({
      name: "School",
      score: scoreDesc(osmDist, [[500, 100], [800, 80], [1200, 60], [2000, 40], [3000, 20]]),
      available: true,
      weight: 1.5,
      reliability: 0.7,
    });
  } else {
    factors.push({ name: "School", score: 0, available: false, weight: 1.5 });
  }

  // Factor 3c: Secondary School — nearest VO (voortgezet onderwijs) school,
  // preferring one rated Goed/Voldoende/Basistoezicht. Separate from the
  // primary-school factor because (a) NL families optimise on primary but
  // commute for secondary, and (b) secondary catchments are bike-scale
  // (5–10 km) vs walk-scale (1–2 km) for primary. Lower weight than primary
  // because only ~15% of NL households have secondary-age kids at any time.
  const voGood = sn?.vo?.nearestGood?.distance;
  const voAny  = sn?.vo?.nearest?.distance;
  if (voGood != null) {
    factors.push({
      name: "Secondary School",
      score: scoreDesc(voGood, [[1000, 95], [2000, 80], [3500, 62], [6000, 42], [9000, 25]]),
      available: true,
      weight: 0.75,
      reliability: 0.9,
    });
  } else if (voAny != null) {
    factors.push({
      name: "Secondary School",
      score: scoreDesc(voAny, [[1000, 90], [2000, 75], [3500, 55], [6000, 35], [9000, 20]]),
      available: true,
      weight: 0.75,
      reliability: 0.8,
    });
  } else {
    factors.push({ name: "Secondary School", score: 0, available: false, weight: 0.75 });
  }

  // Factor 3b: School Quality — distance-weighted Inspectie verdict mean
  // across schools within 2 km. Orthogonal to School Access: a plot can
  // sit 400 m from one school but have four Onvoldoende-rated schools in
  // its catchment, and School Quality is what surfaces that.
  //
  // Only scored when at least one school within 2 km has a verdict. The
  // distance-weighted mean from /api/schools-nearby is already 0–100, so
  // map it nearly linearly — just stretch so that 50 (= Voldoende baseline)
  // sits at score 55 (schools are generally fine in NL; you need meaningful
  // quality evidence to pull below that).
  const sq = sn?.loaded ? sn.avgQuality : undefined;
  const hasScoredSchoolsNearby = sn?.distribution && Object.keys(sn.distribution).length > 0;
  if (sq != null && hasScoredSchoolsNearby) {
    factors.push({
      name: "School Quality",
      score: scoreAsc(sq, [[15, 8], [25, 20], [40, 38], [55, 58], [65, 72], [78, 85], [88, 95]]),
      available: true,
      weight: 1.25,
      reliability: 0.75, // NL Inspectie covers ~18% of PO schools at any time
    });
  } else {
    factors.push({ name: "School Quality", score: 0, available: false, weight: 1.25, reliability: 0.75 });
  }

  // Factor 4: Healthcare — occasional use
  const healthDist = amenityDist(plot, "healthcare");
  if (healthDist != null) {
    factors.push({ name: "Healthcare", score: scoreDesc(healthDist, [[500, 100], [800, 80], [1200, 60], [2000, 40], [3000, 20]]), available: true, weight: 1 });
  } else {
    factors.push({ name: "Healthcare", score: 0, available: false, weight: 1 });
  }

  // Factor 5: Parks — amenity value
  const parkDist = amenityDist(plot, "parks");
  if (parkDist != null) {
    factors.push({ name: "Green Space", score: scoreDesc(parkDist, [[300, 100], [500, 85], [800, 65], [1200, 45], [2000, 25]]), available: true, weight: 1.5 });
  } else {
    factors.push({ name: "Green Space", score: 0, available: false, weight: 1.5 });
  }

  // Factor 6: Noise level (lower = better) — directly affects livability + value
  // BUG FIX (E2): The old code used `climate?.noiseRoad || 0` which treats null AND
  // 0 identically. A true 0 dB reading doesn't exist for road/rail noise, so null is
  // the only realistic "missing" value. We check for null/undefined explicitly rather
  // than using ||0 so that the "API returned no reading" case goes to the low-reliability
  // branch instead of looking like a measured 0 dB → "quiet".
  const climate = plot.climateRisk;
  const roadNoise = climate?.noiseRoad ?? null;
  const railNoise = climate?.noiseRail ?? null;
  const airNoise = climate?.noiseAir ?? null;
  const industryNoise = climate?.noiseIndustry ?? null;
  if (roadNoise != null || railNoise != null || airNoise != null || industryNoise != null) {
    // We have at least one real measurement
    const maxNoise = Math.max(roadNoise ?? 0, railNoise ?? 0, airNoise ?? 0, industryNoise ?? 0);
    factors.push({ name: "Noise", score: scoreDesc(maxNoise, [[40, 95], [48, 80], [53, 65], [58, 50], [63, 35], [70, 20]]), available: true, weight: 2 });
  } else if (climate?.loaded && climate.parts?.noise !== false) {
    // The noise register answered but put no modelled source here — genuinely
    // quiet, or a gap in the model. Low reliability. `parts.noise === false`
    // means it did NOT answer, and that must not read as quiet: it did, for
    // every address in the country, while the old PDOK noise service was dead.
    factors.push({ name: "Noise", score: 80, available: true, weight: 2, reliability: 0.5 });
  } else {
    factors.push({ name: "Noise", score: 0, available: false, weight: 2 });
  }

  // Factor 7: Air quality — prefer Luchtmeetnet station data (measured values, higher reliability)
  // over the PDOK NSL 3-bucket categorical. Luchtmeetnet gives actual µg/m³ from the nearest
  // monitoring station; PDOK NSL is a modelled annual-mean grid.
  const lm = plot.luchtmeetnet;
  if (lm?.loaded && (lm.no2 != null || lm.pm25 != null)) {
    // Score based on WHO guidelines: NO₂ annual guideline 10 µg/m³, PM2.5 guideline 5 µg/m³
    // NL values typically: NO₂ 10-35 µg/m³, PM2.5 8-18 µg/m³
    const no2Score = lm.no2 != null ? scoreDesc(lm.no2, [[5, 98], [10, 90], [15, 78], [20, 65], [30, 45], [40, 25]]) : null;
    const pm25Score = lm.pm25 != null ? scoreDesc(lm.pm25, [[3, 98], [5, 90], [8, 75], [12, 58], [18, 38], [25, 20]]) : null;
    // Use worst of the two (bottleneck approach)
    const scores = [no2Score, pm25Score].filter((s): s is number => s != null);
    const aqScore = scores.length > 0 ? Math.min(...scores) : 60;
    // Higher reliability (0.85) since these are actual station measurements, not modelled grids
    // Down-weight slightly because station may be a few km away
    const stationReliability = lm.stationDistance != null && lm.stationDistance < 5000 ? 0.85 : 0.7;
    factors.push({ name: "Air Quality", score: aqScore, available: true, weight: 1.5, reliability: stationReliability });
  } else if (climate?.airQuality) {
    // Fallback to PDOK NSL 3-bucket categorical
    const airScores: Record<string, number> = { good: 90, moderate: 55, poor: 20 };
    factors.push({ name: "Air Quality", score: airScores[climate.airQuality] ?? 60, available: true, weight: 1.5, reliability: 0.7 });
  } else {
    factors.push({ name: "Air Quality", score: 0, available: false, weight: 1.5, reliability: 0.7 });
  }

  // Factor 8: Restaurants — lifestyle / urban signal
  const restDist = amenityDist(plot, "restaurants");
  if (restDist != null) {
    factors.push({ name: "Restaurants", score: scoreDesc(restDist, [[200, 100], [400, 85], [700, 65], [1200, 40], [2000, 20]]), available: true, weight: 1 });
  } else {
    factors.push({ name: "Restaurants", score: 0, available: false, weight: 1 });
  }

  // Factor 9: Fitness / Gyms — niche lifestyle
  const gymDist = amenityDist(plot, "gyms");
  if (gymDist != null) {
    factors.push({ name: "Fitness", score: scoreDesc(gymDist, [[300, 100], [600, 80], [1000, 60], [1500, 40], [2500, 20]]), available: true, weight: 0.5 });
  } else {
    factors.push({ name: "Fitness", score: 0, available: false, weight: 0.5 });
  }

  // Factor 9b: EV charging density (OSM) — forward-looking infrastructure signal.
  //
  // Primary signal is count within 1 km (walk-reach for most EV owners).
  // Small boost when there's a charger within 250 m ("on your street"), and a
  // small penalty when the nearest is >2 km (effectively rural for EV use).
  // Reliability is 0.8 because (a) OSM charger coverage is high in NL but not
  // complete, and (b) the causal link to property value is forward-looking —
  // the signal strengthens as the 2030 new-ICE ban approaches.
  //
  // Anchors calibrated to NL density observed in practice:
  //   0   within 1 km → 25  (rural)
  //   1                → 42
  //   3                → 55
  //   8                → 68
  //   20               → 80
  //   50               → 90
  //   100+             → 96  (dense urban, e.g. Amsterdam centrum)
  const ev = plot.evChargers;
  if (ev?.loaded && ev.count1km != null) {
    let evScore = scoreAsc(ev.count1km, [[0, 25], [1, 42], [3, 55], [8, 68], [20, 80], [50, 90], [100, 96]]);
    if (ev.nearestDistance != null) {
      if (ev.nearestDistance <= 250) evScore = Math.min(100, evScore + 5);
      else if (ev.nearestDistance > 2000) evScore = Math.max(15, evScore - 8);
    }
    factors.push({ name: "EV Access", score: evScore, available: true, weight: 1, reliability: 0.8 });
  } else {
    factors.push({ name: "EV Access", score: 0, available: false, weight: 1, reliability: 0.8 });
  }

  // Factor 10: Green coverage — per-plot measurement from PDOK BGT
  // `begroeidterreindeel`, with the CBS buurt-level `landUseGreenPct` kept
  // as fallback for any plot where BGT hasn't loaded.
  //
  // Why the switch: CBS reports green at buurt aggregate, which averages
  // over the whole neighbourhood — a specific address surrounded by
  // concrete can score the same as an address on the park edge if they
  // share a buurt. BGT gives us the actual fraction of vegetated ground
  // within 500 m of the plot, at 0.5 m parcel resolution, so it's what a
  // visitor would see if they walked around the block.
  //
  // Tree canopy carries the documented NL price premium (a tree-lined
  // street commands ~5–8% over a bare-asphalt street in the same buurt),
  // so we add a bonus when the vegetation is canopy rather than grass.
  const green = plot.greenness;
  if (green?.loaded && green.vegetatedFractionPct != null) {
    // Anchors: 0% → 15, 5% → 35, 15% → 55, 30% → 72, 50% → 85, 70% → 95
    // Calibrated to NL-realistic distributions — urban avg ~15%, suburb
    // ~30%, park-adjacent ~50%+.
    let gScore = scoreAsc(green.vegetatedFractionPct, [[0, 15], [5, 35], [15, 55], [30, 72], [50, 85], [70, 95]]);
    // Tree-canopy bonus: when >40% of the vegetation is tree canopy, NL
    // buyers perceive the area as genuinely "groen" rather than just
    // "unbuilt". Cap the bonus at +5 so it doesn't overwhelm the main signal.
    if (green.treeCanopyAreaM2 && green.vegetatedAreaM2
        && (green.treeCanopyAreaM2 / green.vegetatedAreaM2) >= 0.4) {
      gScore = Math.min(100, gScore + 5);
    }
    factors.push({
      name: "Green Coverage",
      score: gScore,
      available: true,
      weight: 1.25,
      reliability: 0.9, // BGT is the NL authoritative ground-truth, manually curated
    });
  } else {
    // Legacy fallback to CBS buurt-level greenness.
    const greenPct = plot.neighborhood?.landUseGreenPct;
    if (greenPct != null) {
      factors.push({
        name: "Green Coverage",
        score: scoreAsc(greenPct, [[5, 20], [10, 35], [20, 50], [30, 70], [40, 85], [50, 90]]),
        available: true,
        weight: 1,
        reliability: 0.6, // buurt-aggregate proxy — known to over/undercount specific addresses
      });
    } else {
      factors.push({ name: "Green Coverage", score: 0, available: false, weight: 1 });
    }
  }

  // Factor 10b: Street trees — the canopy the terrain measure cannot see.
  //
  // Green Coverage above is BGT `begroeidterreindeel`: vegetated TERRAIN. A
  // street tree does not stand in any — it stands in paved roadway — so the
  // one thing that factor's own comment claims to price, a tree-lined street,
  // is invisible to it. Measured 2026-09-08 on the Prinsengracht, among the
  // most famously tree-lined canals in Amsterdam: 111 trees within 150 m and
  // a BGT canopy fraction of 0.0%. The engine read bare asphalt. BGT holds
  // the trees themselves as `vegetatieobject_punt`, and that is what this
  // scores. The two are complementary, not redundant: Green Coverage answers
  // "is there greenery around here", this answers "is there a tree outside".
  //
  // ANCHORS ARE MEASURED, not guessed (§Marketing claims). 130 random points
  // with >=25 buildings within 150 m — i.e. actual streets, since sampling NL
  // uniformly is mostly farmland — gave trees per hectare of:
  //   p50 3.3 · p75 16.7 · p90 34.4 · p95 50.8 · p99 69.6, and 40% with none.
  //
  // That 40% is why the floor is 40 and not 10. Having no street tree is the
  // ordinary condition of a large minority of Dutch streets, so it is a
  // missing amenity rather than a defect, and the differentiating signal is
  // the upside. Re-run scripts/sample-street-trees.mjs to recalibrate.
  const trees = plot.greenness;
  if (trees?.loaded && trees.streetTreesCovered && trees.streetTreesPerHa != null) {
    let tScore = scoreAsc(trees.streetTreesPerHa, [
      [0, 40], [3.3, 55], [16.7, 72], [34.4, 85], [50.8, 92], [69.6, 97],
    ]);
    // Density over a 150 m circle can be high while every tree sits on the
    // far side of it. A tree within 25 m — about the median distance among
    // streets that have one at all — is the doorstep case the price premium
    // actually describes. Capped at +5, like the canopy bonus above, so it
    // adjusts the reading rather than replacing it.
    if (trees.streetTreeNearestM != null && trees.streetTreeNearestM <= 25) {
      tScore = Math.min(100, tScore + 5);
    }
    factors.push({
      name: "Street Trees",
      score: Math.round(tScore),
      available: true,
      weight: 1,
      // BGT is municipality-curated ground truth, but how completely a given
      // gemeente registers its trees varies, and a 150 m circle is a proxy
      // for "your street" rather than the street itself.
      reliability: 0.85,
    });
  } else {
    factors.push({ name: "Street Trees", score: 0, available: false, weight: 1, reliability: 0.85 });
  }

  // Factor 11: Heat stress — growing NL concern (4-bucket categorical → reliability 0.7)
  if (climate?.heatStress) {
    const heatScores: Record<string, number> = { low: 90, moderate: 60, high: 35, very_high: 15 };
    factors.push({ name: "Heat Stress", score: heatScores[climate.heatStress] ?? 50, available: true, weight: 1.5, reliability: 0.7 });
  } else {
    factors.push({ name: "Heat Stress", score: 0, available: false, weight: 1.5, reliability: 0.7 });
  }

  // (Leefbaarometer "Livability" factor removed from Location in the 2026-04
  // methodology pass — it now lives only in Neighborhood. The overallScore
  // and residentsScore were previously scored into both sub-scores, which
  // double-counted a correlated government index. Leefbaarometer is a
  // neighborhood livability measure, so Neighborhood is its natural home.)

  // Factor 11b: View Premium — visible water + park (viewshed ray trace).
  //
  // Three orthogonal viewshed signals. All are Location-layer because they
  // describe the immediate environment seen from the property, not the
  // building or neighbourhood stats. Each uses reliability 0.85 — the ray
  // trace is a solid local model but has known limitations (flat-terrain
  // assumption, level-based height estimation for ~95% of OSM buildings).
  //
  // View Premium composes water + park visibility. Water weighs more than
  // park by NL-market convention (canals, IJ, Maas, polder lakes all carry
  // a documented ~10-15% price premium where direct-view).
  const vs = plot.viewshed;
  if (vs?.loaded && (vs.waterVisibleM2 != null || vs.parkVisibleM2 != null)) {
    // Water component: distance-decayed area.
    let waterScore = 15;
    if (vs.nearestWaterDistM != null && vs.waterVisibleM2 != null) {
      // Area anchors: 0 → 15, 500 → 55, 2000 → 75, 10000 → 90, 50000 → 98
      const areaScore = scoreAsc(vs.waterVisibleM2, [[0, 15], [500, 55], [2000, 75], [10000, 90], [50000, 98]]);
      // Distance decay: a visible canal 2 km away is worth less than the
      // same canal 100 m away. Multiplier ramps from 0.55 at 1.5 km to 1.0
      // at ≤200 m.
      const mult = vs.nearestWaterDistM <= 200 ? 1
                : vs.nearestWaterDistM >= 1500 ? 0.55
                : 1 - (vs.nearestWaterDistM - 200) / 1300 * 0.45;
      waterScore = Math.round(Math.max(15, areaScore * mult));
    }
    // Park component: same shape with smaller coefficient.
    let parkScore = 30;
    if (vs.nearestParkDistM != null && vs.parkVisibleM2 != null) {
      const areaScore = scoreAsc(vs.parkVisibleM2, [[0, 30], [1000, 55], [5000, 72], [20000, 85], [100000, 94]]);
      const mult = vs.nearestParkDistM <= 250 ? 1
                : vs.nearestParkDistM >= 1500 ? 0.6
                : 1 - (vs.nearestParkDistM - 250) / 1250 * 0.4;
      parkScore = Math.round(Math.max(30, areaScore * mult));
    }
    // Combine: take the better of the two, plus a modest bonus when both
    // are present — a plot with BOTH water and park views is rarer than
    // either alone.
    let viewScore = Math.max(waterScore, parkScore);
    if (waterScore > 50 && parkScore > 50) viewScore = Math.min(100, viewScore + 5);
    factors.push({ name: "View Premium", score: viewScore, available: true, weight: 1.25, reliability: 0.85 });
  } else {
    factors.push({ name: "View Premium", score: 0, available: false, weight: 1.25, reliability: 0.85 });
  }

  // Factor 11c: Sky View Factor — hemispheric openness.
  //
  // SVF captures daylight, privacy, and the "canyon effect". Anchors are
  // calibrated to the 10 m eye height our ray trace uses (ground-level
  // fish-eye SVF literature values don't apply). Empirical NL at this eye
  // height (from smoke tests):
  //   Silodam waterfront → 0.995
  //   Jordaan canyon     → 0.95
  //   Suburban           → 0.97–0.98
  //   Rural              → 0.99+
  // The score spread is concentrated at the top of the SVF range because
  // looking DOWN on a city at 10 m eye height gives more sky than looking
  // along the street.
  if (vs?.loaded && vs.skyViewFactor != null) {
    factors.push({
      name: "Sky View",
      score: scoreAsc(vs.skyViewFactor, [[0.75, 10], [0.85, 25], [0.90, 45], [0.94, 60], [0.97, 78], [0.99, 92], [0.998, 98]]),
      available: true,
      weight: 1,
      reliability: 0.85,
    });
  } else {
    factors.push({ name: "Sky View", score: 0, available: false, weight: 1, reliability: 0.85 });
  }

  // Factor 11d: Obstruction — how much of the immediate horizon is wall.
  //
  // THIS COUNTS DIRECTIONS, NOT BUILDINGS, and the difference is the whole
  // point. It used to score `nearObstructionCount`, the number of separate
  // buildings within 30 m taller than eye level, which made the result depend
  // on cadastral bookkeeping rather than on anything a resident experiences:
  // an Amsterdam canal terrace is twenty narrow houses in one continuous
  // wall, and it scored as twenty obstructions.
  //
  // Measured 2026-09-08 on Nassaukade 177-1, a corner flat facing the
  // Singelgracht: 21 separate BGT `pand` records within 30 m, which clamped
  // the old count ladder to its floor and then took the >=3-sector penalty on
  // top — about 7 out of 100 for an apartment with open water across the
  // street. Prinsengracht measured 28. The scale could not distinguish "hemmed
  // in on all sides" from "one long terrace and an open canal", because
  // splitting one wall into more parcels made the score worse.
  //
  // `nearObstructionSectors` is the honest quantity: of the eight 45-degree
  // compass sectors, how many contain a near wall. A terrace occupies one or
  // two sectors however many deeds it is divided into, and the canal simply
  // leaves its sectors empty, so open frontage is credited by construction
  // rather than by a special case.
  //
  // The mapping is deliberately a DEFINITION, not a fitted curve: each
  // blocked eighth of the horizon costs ten points. Calibrating it against a
  // real national distribution needs a viewshed sample that Overpass was too
  // unwell to provide today — worth doing, but a fitted-looking ladder chosen
  // by eye would only have disguised that (§Marketing claims).
  //
  // Sky View is the companion factor and stays distinct: it reads the whole
  // hemisphere out to 600 m ("how much sky"), where this reads the walls
  // within 30 m ("how close are they").
  if (vs?.loaded && vs.nearObstructionSectors != null) {
    const blocked = Math.max(0, Math.min(8, vs.nearObstructionSectors));
    factors.push({
      name: "Obstruction",
      score: 95 - 10 * blocked,
      available: true,
      weight: 1,
      reliability: 0.85,
    });
  } else {
    factors.push({ name: "Obstruction", score: 0, available: false, weight: 1, reliability: 0.85 });
  }


  // Factor 11e: Sunshine — KNMI 30-year normal (1991–2020) at nearest station.
  //
  // NL annual sunshine ranges ~1600 h (NE interior) to ~1890 h (Zeeland coast),
  // a ~20% spread with clear regional premium for the coast. This is the
  // "bright-side" feel of a location that PVGIS (which we use for solar yield)
  // doesn't separately expose as a lifestyle signal.
  const cn = plot.climateNormals;
  if (cn?.loaded && cn.sunshineHours != null) {
    factors.push({
      name: "Sunshine",
      score: scoreAsc(cn.sunshineHours, [[1550, 25], [1620, 40], [1680, 55], [1730, 68], [1800, 82], [1890, 95]]),
      available: true,
      weight: 0.5,
      reliability: 0.85, // nearest-station is a good proxy in flat NL
    });
  } else {
    factors.push({ name: "Sunshine", score: 0, available: false, weight: 0.5, reliability: 0.85 });
  }

  // Factor 11f: Connectivity — mobile-tower density (OSM antennas proxy).
  //
  // The primary scoring direction is DOWNSIDE: a plot with zero towers in 2 km
  // is a genuine rural-coverage outlier in NL. Upside is capped because towers
  // ≠ bandwidth and OSM doesn't capture dense 5G small-cells, so a typical
  // urban 10-towers-in-2km plot already saturates the signal.
  const con = plot.connectivity;
  if (con?.loaded && con.count2km != null) {
    let cScore = scoreAsc(con.count2km, [[0, 25], [2, 45], [5, 62], [10, 75], [20, 85], [40, 92]]);
    if (con.has5g) cScore = Math.min(100, cScore + 5);
    factors.push({ name: "Connectivity", score: cScore, available: true, weight: 0.5, reliability: 0.7 });
  } else {
    factors.push({ name: "Connectivity", score: 0, available: false, weight: 0.5, reliability: 0.7 });
  }

  // Factor 12: Distance to nearest economic centre (CBD / job hub)
  // Captures "does this location put you within commutable reach of a major job market?"
  // 5km = central urban, 15km = suburban, 30km = commuter belt, 60km+ = rural
  const cbd = nearestCbdDistance(plot);
  if (cbd) {
    factors.push({
      name: "Job Centre",
      score: scoreDesc(cbd.distanceKm, [[5, 95], [10, 80], [20, 65], [35, 50], [60, 30], [100, 15]]),
      available: true,
      weight: 2,
    });
  } else {
    factors.push({ name: "Job Centre", score: 0, available: false, weight: 2 });
  }

  return computeSubScore(factors);
}

function scoreNeighborhood(plot: PlotData): SubScore {
  const factors: SubScore["factors"] = [];
  const hood = plot.neighborhood;

  // Factor 1: Average income (NL median ~€30k) — wealth proxy, correlated with SES (down-weighted)
  const income = hood?.gemiddeldInkomen;
  if (income != null && income > 0) {
    factors.push({ name: "Avg Income", score: scoreAsc(income, [[20, 20], [25, 35], [30, 50], [35, 65], [42, 75], [50, 85], [65, 95]]), available: true, weight: 1.5 });
  } else {
    factors.push({ name: "Avg Income", score: 0, available: false, weight: 1.5 });
  }

  // Factor 2: Ownership ratio — stability signal
  const ownership = hood?.percentageKoopwoningen;
  if (ownership != null && ownership > 0) {
    factors.push({ name: "Ownership %", score: scoreAsc(ownership, [[20, 20], [35, 35], [45, 50], [55, 60], [65, 75], [80, 90]]), available: true, weight: 1.5 });
  } else {
    factors.push({ name: "Ownership %", score: 0, available: false, weight: 1.5 });
  }

  // Factor 3: Neighborhood WOZ (CBS avg) — wealth proxy, correlated with Income/SES (down-weighted)
  const hoodWoz = hood?.gemWozWaarde;
  if (hoodWoz != null && hoodWoz > 0) {
    factors.push({ name: "Area WOZ", score: scoreAsc(hoodWoz / 1000, [[150, 20], [200, 30], [250, 45], [300, 55], [400, 70], [500, 80], [700, 90]]), available: true, weight: 1 });
  } else {
    factors.push({ name: "Area WOZ", score: 0, available: false, weight: 1 });
  }

  // Factor 4: Population density — context-dependent
  const density = hood?.bevolkingsdichtheid;
  if (density != null && density > 0) {
    // Sweet spot: 3000-8000/km² for urban NL
    let s: number;
    if (density < 500) s = 30;
    else if (density < 2000) s = 50;
    else if (density < 5000) s = 75;
    else if (density < 10000) s = 65;
    else s = 45;
    factors.push({ name: "Density", score: s, available: true, weight: 1 });
  } else {
    factors.push({ name: "Density", score: 0, available: false, weight: 1 });
  }

  // Factor 5: SES score (CBS 85539NED — 1-100, 50=NL median) — best single wealth composite
  const ses = hood?.sesDecile;
  if (ses != null) {
    factors.push({ name: "SES Score", score: scoreAsc(ses, [[15, 10], [25, 25], [35, 40], [45, 55], [55, 70], [65, 80], [75, 90], [85, 98]]), available: true, weight: 2 });
  } else {
    factors.push({ name: "SES Score", score: 0, available: false, weight: 2 });
  }

  // Factor 6: Education level (CBS 86232NED — % HBO/WO, NL avg ~30%) — already baked into SES
  const edu = hood?.educationPctHboWo;
  if (edu != null) {
    factors.push({ name: "Education", score: scoreAsc(edu, [[10, 15], [20, 30], [30, 50], [40, 65], [50, 80], [60, 90]]), available: true, weight: 1 });
  } else {
    factors.push({ name: "Education", score: 0, available: false, weight: 1 });
  }

  // Factor 7: Safety — CBS crime data. When detailed crime-by-type is available (47018NED),
  // break into sub-factors for a more nuanced safety picture. Otherwise use total crime rate.
  //
  // EVERY CBS CRIME RATE HERE IS PER 1000 RESIDENTS, and that denominator is
  // the whole difficulty. In a shopping or office buurt almost nobody lives
  // there while the crimes counted are against visitors, so the "rate"
  // describes footfall, not the experience of living there. Kalverdriehoek —
  // the Kalverstraat — has about 250 residents and 317 recorded crimes, so
  // CBS publishes 1268 per 1000 against a national gemeente median of 24.
  // Scored naively that is the floor for every address in it, and printed
  // naively it was "−43280% vs NL avg" (reported 2026-09-10).
  //
  // So a rate needs enough residents to be about residents. Below
  // MIN_CRIME_RESIDENTS the three factors report UNAVAILABLE rather than
  // scoring: unavailable is the truth ("this figure is not about living
  // here") where a floor score is a claim we cannot support, and it feeds
  // confidence honestly. 500 is a judgement, not a measurement — it is the
  // point below which one extra crime moves the published rate by two full
  // points and the visitor:resident ratio in a Dutch city centre is already
  // an order of magnitude.
  const MIN_CRIME_RESIDENTS = 500;
  const crimeResidents = hood?.crimeRateResidents ?? hood?.aantalInwoners ?? null;
  const crimeRatesAreAboutResidents = crimeResidents == null || crimeResidents >= MIN_CRIME_RESIDENTS;

  const crime = hood?.crimeRate;
  const hasCrimeDetail = hood?.propertyCrimeRate != null || hood?.violentCrimeRate != null;
  if (!crimeRatesAreAboutResidents) {
    // Named so the panel can say WHY, and weighted as they would have been so
    // the confidence drop is the real one.
    factors.push({ name: "Property Crime", score: 0, available: false, weight: 1.5 });
    factors.push({ name: "Violent Crime", score: 0, available: false, weight: 1.5 });
    factors.push({ name: "Vandalism", score: 0, available: false, weight: 0.5 });
  } else if (hasCrimeDetail) {
    // ── Ladders measured, not guessed: the distribution across all 380
    //    gemeenten (CBS 84468NED, read 2026-09-10) is
    //      property  p10 10 · median 17 · p90 29   (Amsterdam 67)
    //      violent   p10  2 · median  3 · p90  5   (Amsterdam  5)
    //      vandalism p10  2 · median  3 · p90  6   (Amsterdam  9)
    //    The old anchors were written for a burglary-only rate and for
    //    violent/vandalism figures that arrived divided by ten, so property
    //    crime sat on its floor in every city and violent crime scored ~95
    //    everywhere. A national median should land mid-scale, and now does.
    if (hood!.propertyCrimeRate != null) {
      factors.push({ name: "Property Crime", score: scoreDesc(hood!.propertyCrimeRate, [[8, 95], [12, 85], [17, 70], [24, 50], [35, 30], [60, 15]]), available: true, weight: 1.5 });
    }
    if (hood!.violentCrimeRate != null) {
      factors.push({ name: "Violent Crime", score: scoreDesc(hood!.violentCrimeRate, [[1.5, 95], [2.5, 85], [3.5, 70], [5, 50], [8, 30], [14, 15]]), available: true, weight: 1.5 });
    }
    if (hood!.vandalismRate != null) {
      factors.push({ name: "Vandalism", score: scoreDesc(hood!.vandalismRate, [[1.5, 92], [3, 75], [5, 55], [8, 35], [14, 18]]), available: true, weight: 0.5 });
    }
  } else if (crime != null) {
    // Fallback: total crime rate (single factor, higher weight to compensate).
    // Its anchors already sat on the right scale — the national median of 24
    // lands at ~92 — so they are untouched.
    factors.push({ name: "Safety", score: scoreDesc(crime, [[20, 95], [35, 80], [50, 65], [70, 50], [100, 35], [150, 20]]), available: true, weight: 2.5 });
  } else {
    factors.push({ name: "Safety", score: 0, available: false, weight: 2.5 });
  }

  // Factor 7b: Crime Trend — direction of travel from CBS 47018NED (2021–2025).
  //
  // The static Burglary/Violent/Vandalism factors above use the legacy
  // 84468NED whose last data point is 2018. Crime Trend reads the current
  // politie/CBS table and asks the question those cannot: "is this buurt
  // getting safer or more dangerous?" A Centrum-grade absolute level with
  // an 8% yoy fall signals a gentrifying area; a low-crime suburb with a
  // 10% yoy rise signals a deteriorating one. The prior crime factors are
  // explicitly kept as-is — level matters — but this factor is the direction.
  //
  // When we have cagrPct we use it as a continuous signal; otherwise we fall
  // back to the 4-bucket classification from the route. Reliability 0.85 —
  // CBS 5y series is high quality but small-buurt year-to-year noise is real.
  const ct = plot.crimeTrend;
  if (ct?.loaded && ct.cagrPct != null) {
    factors.push({
      name: "Crime Trend",
      score: scoreDesc(ct.cagrPct, [[-8, 92], [-3, 78], [0, 62], [3, 42], [8, 22], [15, 10]]),
      available: true,
      weight: 1,
      reliability: 0.85,
    });
  } else if (ct?.loaded && ct.trend) {
    const trendScores: Record<string, number> = {
      falling: 85, stable: 60, rising: 35, surging: 15,
    };
    factors.push({ name: "Crime Trend", score: trendScores[ct.trend] ?? 50, available: true, weight: 1, reliability: 0.75 });
  } else {
    factors.push({ name: "Crime Trend", score: 0, available: false, weight: 1, reliability: 0.85 });
  }

  // Factor 7c: Crime Shift (Short-term) — last 12 months vs prior 12 months,
  // from CBS 47022NED (monthly). Catches acute buurt-level shifts that the
  // 5-year annual CAGR smooths away: a new nightlife venue, a closed police
  // station, a gentrification wave that started 8 months ago. Only scored
  // when we have ≥18 months of non-suppressed monthly data — below that the
  // small-cell noise dominates. Weight 0.5: orthogonal direction signal, but
  // noisier than the annual one.
  const cm = ct?.monthly;
  if (cm && cm.yoyPct != null) {
    factors.push({
      name: "Crime Shift",
      score: scoreDesc(cm.yoyPct, [[-20, 90], [-8, 75], [0, 62], [8, 42], [20, 22], [40, 8]]),
      available: true,
      weight: 0.5,
      reliability: 0.7,
    });
  } else {
    factors.push({ name: "Crime Shift", score: 0, available: false, weight: 0.5, reliability: 0.7 });
  }

  // Factor 8: Labor participation (CBS 86258NED — NL avg ~70%) — economic vitality
  const labor = hood?.laborParticipation;
  if (labor != null) {
    factors.push({ name: "Labor Rate", score: scoreAsc(labor, [[55, 20], [60, 35], [65, 50], [70, 65], [75, 80], [80, 90]]), available: true, weight: 1 });
  } else {
    factors.push({ name: "Labor Rate", score: 0, available: false, weight: 1 });
  }

  // Factor 9: New construction activity — developer confidence signal
  const nieuwbouw = hood?.nieuwbouwWoningen;
  if (nieuwbouw != null) {
    factors.push({ name: "New Construction", score: scoreAsc(nieuwbouw, [[0, 30], [20, 40], [50, 55], [100, 65], [250, 80], [500, 90]]), available: true, weight: 1 });
  } else {
    factors.push({ name: "New Construction", score: 0, available: false, weight: 1 });
  }

  // Factor 10: Leefbaarometer (government livability 1-5, 100m resolution)
  // — the composite overall score, which aggregates five dimensions (residents,
  // housing stock, services, safety, physical environment). Previously this
  // sub-score used the narrower residentsScore while Location pulled in the
  // overallScore, which double-counted the same government index across two
  // sub-scores. The richer overallScore is a better single signal and is now
  // Neighborhood's exclusive home for Leefbaarometer data.
  //
  // WEIGHT 3 — the heaviest factor in this layer (2026-09-09, Josh's call to
  // put it above Safety's 2.5). Two reasons it earns that. It is the only
  // neighborhood reading taken at 100 m rather than per buurt, so it is the
  // one that actually distinguishes two ends of the same buurt. And it is a
  // composite of five measured dimensions — housing, residents, facilities,
  // safety, physical environment — where the factors around it each speak to
  // one. Note Safety is a FALLBACK that only fires when the crime breakdown
  // is missing; the cluster it competes with is Burglary + Violent Crime +
  // Vandalism + Crime Trend + Crime Shift (5.0 between them), and the
  // Leefbaarometer's own safety dimension already sits inside this factor.
  //
  // SCORED ON THE 1-9 CLASS, not the 1-5 collapse: the route ships the
  // published national class and the five 5-scale numbers the UI prints, so
  // the engine can use the finer one. The five anchors that existed keep
  // their value (1→10, 3→30, 5→55, 7→80 was 75, 9→96 was 95) and the even
  // classes take the midpoints they used to be rounded into, which is where
  // the granularity was being lost: a class 8 "goed" scored the same 95 as a
  // class 9 "uitstekend", and a class 4 the same 30 as a class 3.
  const lbm = plot.leefbaarometer;
  if (lbm?.classScore9 != null) {
    factors.push({
      name: "Livability",
      score: scoreAsc(lbm.classScore9, [[1, 10], [2, 20], [3, 30], [4, 42], [5, 55], [6, 68], [7, 80], [8, 90], [9, 96]]),
      available: true,
      weight: 3,
      // A buurt-level reading is the same index measured over a much larger
      // area, so it answers a coarser question than the factor claims to.
      reliability: lbm.scale === "buurt" ? 0.8 : 1,
    });
  } else if (lbm?.overallScore != null) {
    // Older cached readings carry only the 5-scale collapse.
    factors.push({ name: "Livability", score: scoreAsc(lbm.overallScore, [[1, 10], [2, 30], [3, 55], [4, 75], [5, 95]]), available: true, weight: 3, reliability: 0.9 });
  } else {
    factors.push({ name: "Livability", score: 0, available: false, weight: 3 });
  }

  // Factor 11: Business growth (CBS) — economic dynamism
  const bizGrowth = hood?.businessGrowthPct;
  if (bizGrowth != null) {
    factors.push({ name: "Business Growth", score: scoreAsc(bizGrowth, [[-5, 20], [-2, 35], [0, 50], [2, 65], [5, 80], [8, 90]]), available: true, weight: 1 });
  } else {
    factors.push({ name: "Business Growth", score: 0, available: false, weight: 1 });
  }

  // Factor 12: Income trajectory (5-year CAGR from CBS 86161NED)
  // Captures gentrification / decline. NL median municipal income grows ~1-2%/year nominal.
  const incomeGrowth = hood?.incomeGrowth5yPct;
  if (incomeGrowth != null) {
    factors.push({
      name: "Income Trend",
      score: scoreAsc(incomeGrowth, [[-2, 15], [0, 30], [1, 45], [2, 60], [3, 75], [5, 90]]),
      available: true,
      weight: 1.5,
    });
  } else {
    factors.push({ name: "Income Trend", score: 0, available: false, weight: 1.5 });
  }

  // Factor 13: Population trajectory (5-year historical CAGR from CBS 85171NED)
  // Distinct from "Pop. Forecast" in Investment which is forward-looking (2030 forecast).
  // Historical trend shows whether a buurt is currently growing or shrinking.
  const popHist = hood?.population5yGrowthPct;
  if (popHist != null) {
    factors.push({
      name: "Population Trend",
      score: scoreAsc(popHist, [[-2, 15], [-0.5, 35], [0.2, 50], [1, 65], [2, 80], [3.5, 92]]),
      available: true,
      weight: 1.5,
    });
  } else {
    factors.push({ name: "Population Trend", score: 0, available: false, weight: 1.5 });
  }

  return computeSubScore(factors);
}

function scoreInvestment(plot: PlotData): SubScore {
  const factors: SubScore["factors"] = [];
  const woz = plot.woz;
  const market = plot.market;

  // Factor 1: Market price trend (municipality CAGR) — hardest financial signal
  const mCagr = market?.pricePerSqmHistory ? computeMarketCAGR(market.pricePerSqmHistory) : null;
  if (mCagr != null) {
    factors.push({ name: "Market Trend", score: scoreAsc(mCagr, [[-2, 10], [0, 25], [2, 45], [4, 60], [6, 75], [8, 85], [12, 95]]), available: true, weight: 3 });
  } else {
    factors.push({ name: "Market Trend", score: 0, available: false, weight: 3 });
  }

  // Factor 2: WOZ CAGR — property-specific price trend
  // The WOZ history we hold is the BUILDING's, and every unit in a block is
  // revalued off it, so it is a fair proxy for any of them — but only a proxy,
  // and the reliability says so.
  //
  // It used to reach the first address only (`index === 0` in projectUnit), so
  // two identical flats in one building scored differently, and carried
  // different confidence, purely by their position in the address array. That
  // ordering means nothing to a buyer, and nothing in the product exposed it.
  const wCagr = woz?.waardeHistorie ? computeCAGR(woz.waardeHistorie) : null;
  if (wCagr != null) {
    factors.push({
      name: "Value Growth",
      score: scoreAsc(wCagr, [[-2, 10], [0, 25], [2, 45], [4, 65], [6, 80], [10, 95]]),
      available: true,
      weight: 2,
      reliability: isUnitOfMultiUnitBuilding(plot) ? 0.75 : 1,
    });
  } else {
    factors.push({ name: "Value Growth", score: 0, available: false, weight: 2 });
  }

  // Factor 3: Price index YoY — short-term momentum
  const yoy = market?.priceIndexYoY;
  if (yoy != null) {
    factors.push({ name: "YoY Index", score: scoreAsc(yoy, [[-5, 10], [-2, 25], [0, 40], [3, 55], [5, 70], [8, 80], [12, 90]]), available: true, weight: 1.5 });
  } else {
    factors.push({ name: "YoY Index", score: 0, available: false, weight: 1.5 });
  }

  // Factor 4: Multi-unit potential — rental scalability.
  //
  // ONLY MEANINGFUL WHEN THE WHOLE BUILDING IS THE ASSET. What it prices is
  // "this can be let as several dwellings", and someone buying one apartment
  // acquires none of that: the other flats are not theirs to let, however many
  // there are. So at unit scope the factor is not low, it is INAPPLICABLE.
  //
  // It used to be scored anyway, and always as 1, because `projectUnit`
  // synthesises `unitCount: 1` for the unit it is describing. Every apartment
  // in the country therefore took the bottom anchor — 30, at weight 1.5 —
  // for not being a multi-unit building, while sitting in one. Found on
  // Nassaukade 177-1, an Amsterdam canal apartment, 2026-09-08.
  //
  // `buildingUnitCount` is what distinguishes the two cases: a detached house
  // genuinely has no multi-unit potential and still scores 30, but a flat in a
  // twelve-unit building now reports nothing rather than the wrong thing.
  // Marking it unavailable also feeds confidence honestly — the dossier says
  // one fewer factor was measurable, which is true.
  const unitCount = woz?.unitCount;
  const buildingUnits = woz?.buildingUnitCount;
  const scopedToOneOfMany = unitCount === 1 && buildingUnits != null && buildingUnits > 1;
  if (unitCount != null && !scopedToOneOfMany) {
    factors.push({ name: "Multi-unit", score: scoreAsc(unitCount, [[1, 30], [2, 55], [3, 65], [5, 75], [10, 85], [20, 95]]), available: true, weight: 1.5 });
  } else {
    factors.push({ name: "Multi-unit", score: 0, available: false, weight: 1.5 });
  }

  // Factor 5: Population forecast (CBS 85171NED, growth % to 2030) — demand driver
  const popGrowth = plot.neighborhood?.populationGrowthPct;
  if (popGrowth != null) {
    factors.push({ name: "Pop. Forecast", score: scoreAsc(popGrowth, [[-5, 10], [-2, 25], [0, 40], [2, 55], [5, 70], [8, 85], [12, 95]]), available: true, weight: 1.5 });
  } else {
    factors.push({ name: "Pop. Forecast", score: 0, available: false, weight: 1.5 });
  }

  // Factor 6: Heat-transition readiness (CBS 85677NED) — consolidated signal.
  //
  // Previously three separate factors (gas% dependency weight 1.5, heat-pump
  // adoption weight 0.5, district-heating availability weight 0.5) all pointed
  // the same direction and were strongly anti-correlated (a gas-heavy buurt
  // almost by definition has low heat-pump/district adoption), which triple-
  // counted the same transition signal. The methodology review (2026-04)
  // folded them into one factor whose sub-components contribute proportionally
  // to their independent informativeness. Combined weight is 2.0 — the old sum
  // of 2.5 minus a ~20% correlation discount.
  //
  // Gas dependency is the strongest and most direct signal (stranded-asset
  // risk), so it carries the majority sub-weight. Heat-pump adoption is
  // forward-looking. District heating is infrastructure availability; it
  // matters but only for buurten where it's actually been rolled out.
  const heatComponents: { score: number; weight: number }[] = [];
  const gasPct = plot.neighborhood?.heatingGasPct;
  if (gasPct != null) {
    heatComponents.push({
      score: scoreDesc(gasPct, [[10, 95], [30, 80], [50, 65], [70, 50], [85, 35], [95, 20]]),
      weight: 2,
    });
  }
  const hpPct = plot.neighborhood?.heatingHeatPumpPct;
  if (hpPct != null) {
    heatComponents.push({
      score: scoreAsc(hpPct, [[3, 25], [5, 35], [10, 50], [15, 65], [20, 80], [30, 90]]),
      weight: 1,
    });
  }
  const distPct = plot.neighborhood?.heatingDistrictPct;
  if (distPct != null) {
    heatComponents.push({
      score: scoreAsc(distPct, [[0, 40], [5, 50], [15, 60], [30, 70], [50, 85]]),
      weight: 1,
    });
  }
  if (heatComponents.length > 0) {
    const totalW = heatComponents.reduce((s, c) => s + c.weight, 0);
    const combined = heatComponents.reduce((s, c) => s + c.score * c.weight, 0) / totalW;
    factors.push({ name: "Heat Transition", score: Math.round(combined), available: true, weight: 2 });
  } else {
    factors.push({ name: "Heat Transition", score: 0, available: false, weight: 2 });
  }

  // Factor 7: Zoning flexibility (mixed-use = highest development optionality)
  const primaryZone = plot.zoning?.[0]?.bestemmingshoofdgroep?.toLowerCase();
  if (primaryZone) {
    const flexScores: Record<string, number> = {
      gemengd: 90, wonen: 70, kantoor: 65, detailhandel: 60,
      bedrijf: 50, maatschappelijk: 45, groen: 30, verkeer: 20,
    };
    factors.push({ name: "Zoning Flex", score: flexScores[primaryZone] ?? 50, available: true, weight: 1.5 });
  } else {
    factors.push({ name: "Zoning Flex", score: 0, available: false, weight: 1.5 });
  }

  // Factor 8: Restriction risk (dubbelbestemmingen + WKPB) — hard constraint on upside
  const zd = plot.zoningDetail;
  if (zd?.loaded) {
    let riskScore = 85;
    const nDubbel = zd.dubbelbestemmingen?.length || 0;
    riskScore -= nDubbel * 10;
    for (const r of zd.wkpb || []) {
      const t = r.type.toLowerCase();
      if (t.includes("verontreiniging") || t.includes("bodem")) riskScore -= 20;
      else if (t.includes("monument")) riskScore -= 15;
      else riskScore -= 8;
    }
    factors.push({ name: "Restriction Risk", score: Math.max(10, riskScore), available: true, weight: 2 });
  } else {
    factors.push({ name: "Restriction Risk", score: 0, available: false, weight: 2 });
  }

  // Factor 9: Flood risk — deal-breaker potential (also drives composite cap in computePropertyScore)
  const climate = plot.climateRisk;
  if (climate?.floodRisk) {
    const floodScores: Record<string, number> = { very_low: 95, low: 80, moderate: 55, high: 30, very_high: 10 };
    factors.push({ name: "Flood Risk", score: floodScores[climate.floodRisk] ?? 60, available: true, weight: 2 });
  } else if (climate?.loaded && climate.parts?.flood !== false) {
    // The flood map answered with no land class here (the point and its
    // neighbours are water, or off the map) → low reliability. An unanswered
    // flood part is unavailable, never "safe".
    factors.push({ name: "Flood Risk", score: 90, available: true, weight: 2, reliability: 0.5 });
  } else {
    factors.push({ name: "Flood Risk", score: 0, available: false, weight: 2 });
  }

  // Factor 9c: Seismic risk — Groningen gas-field induced seismicity.
  //
  // Regional — only relevant to the NE corner of NL. Outside the 30 km
  // radius around Loppersum the factor scores neutral-to-high (no risk)
  // and contributes nothing meaningful. Inside, it graduates by distance
  // from the historic epicentre cluster, mirroring KNMI's PGA hazard map.
  // Reliability is 0.75 — the bbox/distance classifier is a coarse proxy
  // for the full KNMI hazard polygons; we'd need their WMS for true-grade
  // accuracy but the 30 km gradient captures >95% of the policy-relevant
  // variation for residential scoring.
  const sr = plot.seismicRisk;
  if (sr?.loaded && sr.riskLevel) {
    const seismicScores: Record<string, number> = {
      negligible: 95, low: 80, moderate: 55, high: 30, very_high: 12,
    };
    factors.push({
      name: "Seismic Risk",
      score: seismicScores[sr.riskLevel] ?? 80,
      available: true,
      weight: 1.5,
      reliability: 0.75,
    });
  } else {
    factors.push({ name: "Seismic Risk", score: 0, available: false, weight: 1.5, reliability: 0.75 });
  }

  // Factor 9b: Foundation risk — funderingsproblematiek (RVO/KCAF via PDOK).
  // The source dataset flags PC6 postcode areas where ≥40% of buildings are
  // pre-1970. Wooden pile foundations in these zones are increasingly vulnerable
  // to pile rot as groundwater drops, and shallow foundations on peat/clay are
  // subject to differential subsidence. The NL remediation cost is material
  // (typically €80–200k per building), which is why this drives a risk cap too.
  //
  // The indicator is PC6-level — it doesn't directly measure THIS building's
  // foundation, only the density of pre-1970 structures around it. When we know
  // the plot's actual construction year we adjust: a post-1990 building sitting
  // in a flagged zone is almost certainly on modern concrete piles, so the
  // signal is much weaker (neighbourhood cost/nuisance, not direct risk to
  // this asset). Reliability is capped at 0.8 because we're proxying per-
  // building foundation type from a neighbourhood-level age distribution.
  const fr = plot.foundationRisk;
  const plotBouwjaar = plot.buildings?.find(b => b?.bouwjaar != null)?.bouwjaar ?? null;
  if (fr?.loaded && fr.inRiskZone === true && fr.riskLevel) {
    const baseScores: Record<string, number> = { low: 75, moderate: 55, high: 32, very_high: 12 };
    let frScore = baseScores[fr.riskLevel] ?? 45;
    // Age-aware adjustment: modern construction is highly unlikely to share the
    // foundation type that makes the zone risky. We pull toward 75 (still not
    // neutral-plus, because surrounding infrastructure / neighbour subsidence
    // can cascade), weighted by how modern the building is.
    if (plotBouwjaar != null) {
      if (plotBouwjaar >= 1990) frScore = Math.round(frScore * 0.3 + 75 * 0.7);
      else if (plotBouwjaar >= 1970) frScore = Math.round(frScore * 0.6 + 70 * 0.4);
    }
    factors.push({ name: "Foundation Risk", score: frScore, available: true, weight: 1.5, reliability: 0.8 });
  } else if (fr?.loaded && fr.inRiskZone === false) {
    // Outside any flagged PC6 — this is the explicit "safe" signal from the dataset.
    factors.push({ name: "Foundation Risk", score: 92, available: true, weight: 1.5, reliability: 0.8 });
  } else {
    factors.push({ name: "Foundation Risk", score: 0, available: false, weight: 1.5, reliability: 0.8 });
  }

  // Factor 9b: Ground motion (bodemdaling), MEASURED.
  //
  // Copernicus EGMS radar interferometry, 100 m, vertical component, baked to
  // a national grid by scripts/build-subsidence.py. Negative mm/yr = sinking.
  //
  // WHY IT SCORES A PERCENTILE AND NOT A RATE. Essentially all of the
  // Netherlands is sinking a little: the measured national median is about
  // -1.07 mm/yr and even the stable top 5% is still slightly negative. So
  // "is it negative?" flags the whole country and "is it worse than -5?"
  // flags almost nobody. What carries information is where this plot sits in
  // the national distribution, and that distribution ships with the reading
  // (`national`), so a re-bake recalibrates this factor without a code change
  // and no threshold here was invented (§Marketing claims).
  //
  // It sits beside Foundation Risk deliberately — same underlying risk, two
  // very different kinds of evidence. Foundation Risk says where wooden piles
  // are LIKELY from a PC6 building-age average; this says what the ground and
  // the structures on it are ACTUALLY doing. Weight is 1.2 against that
  // factor's 1.5: better evidence, but narrower, and stacking two full-weight
  // versions of one risk would double-count it.
  const sub = plot.subsidence;
  if (sub?.loaded && sub.covered && sub.mmPerYear != null && sub.national) {
    const pctile = nationalPercentile(sub.mmPerYear, sub.national);
    // Percentile -> score. Curated, like every other anchor ladder in this
    // file; what is NOT curated is the mm/yr each percentile corresponds to.
    // Flat above p95: the far positive tail is ground RISING, which is not
    // better than stable (differential movement of either sign stresses a
    // structure), but it is 0.1% of the country and we have no evidence base
    // to price it, so it is treated as stable rather than guessed at.
    const subScore = Math.round(scoreAsc(Math.min(pctile, 95), [
      [0.1, 8], [1, 20], [5, 38], [25, 58], [50, 72], [75, 85], [95, 95],
    ]));
    // The reading may come from a neighbouring cell — InSAR only sees
    // radar-stable targets, so a plot beside open ground borrows its
    // neighbour's motion. Confidence falls with that distance rather than the
    // reading being silently presented as local.
    const reliability = sub.distanceM != null && sub.distanceM > 0
      ? Math.max(0.55, 0.9 - (sub.distanceM / 400) * 0.35)
      : 0.9;
    factors.push({
      name: "Ground Motion",
      score: subScore,
      available: true,
      weight: 1.2,
      reliability: Math.round(reliability * 100) / 100,
    });
  } else {
    factors.push({ name: "Ground Motion", score: 0, available: false, weight: 1.2, reliability: 0.9 });
  }

  // Factor 10: Heritage status — double-edged.
  //
  // Rijksmonument status cuts two ways: severe renovation restrictions (bad
  // for developability → this factor lives in Investment) but also a
  // documented ~8–12% price premium for well-preserved character property
  // (good for current value). The investment framing keeps the restriction
  // reading: a developer paying for upside won't get it; a homeowner paying
  // for character will.
  //
  // UNESCO werelderfgoed adds restriction but also the strongest possible
  // heritage cachet (e.g. Amsterdam grachtengordel). We treat it as neutral-
  // to-slightly-negative from the Investment-feasibility angle but less
  // punitive than a plain Rijksmonument because UNESCO sites are already in
  // steady-state stewardship programs with well-understood rules.
  const heritage = plot.heritage;
  if (heritage?.loaded) {
    let heritageScore = 70; // baseline: no designation
    if (heritage.isRijksmonument) heritageScore = 40;            // severe renovation restrictions
    if (heritage.beschermdStadsgezicht) heritageScore -= 15;     // exterior-change controls
    if (heritage.unescoWorldHeritage) heritageScore -= 5;        // steady-state stewardship; small marginal penalty
    factors.push({ name: "Heritage", score: Math.max(10, heritageScore), available: true, weight: 1 });
  } else {
    factors.push({ name: "Heritage", score: 0, available: false, weight: 1 });
  }

  // Factor 11: Tenure mix (share of rental housing in the area).
  // Reframed 2026-06: the old factor scored "more rental = better" (a rental-
  // yield / investor lens). The buy-to-let purchase-ban evidence (Francke/
  // Korevaar/van Bekkum) shows removing investor/rental demand had ~no effect on
  // owner-occupier prices, so for a buyer-facing score a high rental share is at
  // most neutral — plausibly a mild negative (transient population, less owner
  // upkeep). Hence a GENTLE descending slope centred near 50 and a low weight;
  // owner-occupancy upside is already captured by Neighborhood "Ownership %",
  // so this avoids double-counting tenure.
  const huurPct = plot.neighborhood?.percentageHuurwoningen;
  if (huurPct != null) {
    factors.push({ name: "Tenure Mix", score: scoreDesc(huurPct, [[15, 60], [35, 55], [55, 48], [75, 40], [90, 32]]), available: true, weight: 1, reliability: 0.7 });
  } else {
    factors.push({ name: "Tenure Mix", score: 0, available: false, weight: 1, reliability: 0.7 });
  }

  // (Factors 12 "Heat Pump Adoption" and 13 "District Heating" were removed
  // in the 2026-04 methodology pass and folded into the consolidated Heat
  // Transition factor above. They were strongly anti-correlated with gas%
  // and carried very low independent weight (0.5 each), so counting them
  // separately mostly inflated the Investment sub-score's apparent factor
  // count without adding signal.)

  // Factor 12: Net migration (CBS) — demand signal
  const migration = plot.neighborhood?.netMigrationPer1000;
  if (migration != null) {
    factors.push({ name: "Migration Trend", score: scoreAsc(migration, [[-10, 10], [-5, 25], [0, 45], [5, 65], [10, 80], [15, 90]]), available: true, weight: 1.5 });
  } else {
    factors.push({ name: "Migration Trend", score: 0, available: false, weight: 1.5 });
  }

  // Factor 13: Nitrogen / stikstof permit risk — affects developer feasibility most.
  // For homeowner/investor it's a softer signal (resale developability), so weight is moderate.
  const nitrogen = plot.nitrogen;
  if (nitrogen?.loaded && nitrogen.permitRisk) {
    const nitrogenScores: Record<string, number> = { low: 90, moderate: 60, high: 30, very_high: 10 };
    factors.push({
      name: "Nitrogen Risk",
      score: nitrogenScores[nitrogen.permitRisk] ?? 60,
      available: true,
      weight: 1.5,
      reliability: 0.7, // Natura 2000 distance is a coarse proxy for actual AERIUS deposition
    });
  } else {
    factors.push({ name: "Nitrogen Risk", score: 0, available: false, weight: 1.5, reliability: 0.7 });
  }

  return computeSubScore(factors);
}

// ── Core computation ──

// ── Driver / detractor helpers (D1 — explainability) ──

export interface ScoreDriver {
  name: string;
  score: number;
  weight: number;
  /** (score − 50) × weight × reliability — positive = pulling sub-score up, negative = pulling down */
  impact: number;
}

/**
 * Rank a sub-score's factors by weighted impact relative to the neutral 50 midpoint.
 * Returns drivers sorted by impact (most positive first).
 * Unavailable factors are excluded.
 */
export function rankFactorImpact(sub: SubScore): ScoreDriver[] {
  return sub.factors
    .filter(f => f.available)
    .map(f => ({
      name: f.name,
      score: f.score,
      weight: f.weight,
      impact: (f.score - 50) * f.weight * (f.reliability ?? 1),
    }))
    .sort((a, b) => b.impact - a.impact);
}

/** Top N factors pulling a sub-score UP relative to neutral. */
export function getTopDrivers(sub: SubScore, n = 3): ScoreDriver[] {
  return rankFactorImpact(sub).filter(d => d.impact > 0).slice(0, n);
}

/** Top N factors pulling a sub-score DOWN relative to neutral. */
export function getTopDetractors(sub: SubScore, n = 3): ScoreDriver[] {
  return rankFactorImpact(sub).filter(d => d.impact < 0).slice(-n).reverse();
}

/** Names of unavailable factors that would have had non-zero weight. Useful for "limited by missing data" UI. */
export function getMissingFactorNames(sub: SubScore): string[] {
  return sub.factors.filter(f => !f.available && f.weight > 0).map(f => f.name);
}

function computeSubScore(factors: SubScore["factors"]): SubScore {
  // Total possible weight across all factors (reliability=1 ceiling, even unavailable ones)
  const totalWeight = factors.reduce((sum, f) => sum + f.weight, 0);
  if (totalWeight <= 0) {
    return { score: 0, confidence: 0, factors };
  }
  const available = factors.filter(f => f.available);
  if (available.length === 0) {
    return { score: 0, confidence: 0, factors };
  }
  // Score: weighted average over available factors. Reliability does NOT bias the score —
  // it only affects confidence. (Factor weight already controls influence on the score.)
  const availableWeight = available.reduce((sum, f) => sum + f.weight, 0);
  const weightedSum = available.reduce((sum, f) => sum + f.score * f.weight, 0);
  // Confidence: weight × reliability for available factors, divided by max-possible weight.
  // Captures BOTH "is the data here?" AND "how trustworthy is the data when it is here?".
  const effectiveAvailable = available.reduce((sum, f) => sum + f.weight * (f.reliability ?? 1), 0);
  return {
    score: Math.round(weightedSum / availableWeight),
    confidence: effectiveAvailable / totalWeight,
    factors,
  };
}

/**
 * Compute risk signals: hard-cap deal-breakers and non-capping advisory flags.
 *
 * Caps clamp the composite — used when the evidence is per-plot direct
 * (soil contamination registrations) OR when the risk is so severe that no
 * realistic per-asset remediation can be assumed (very-high flood depth,
 * very-high seismic exposure inside the Loppersum cluster).
 *
 * Flags surface as amber advisories — used when the underlying dataset is
 * zone-level / indicative and cannot see whether this specific asset has
 * already been remediated. Foundation risk is the canonical example: the
 * KCAF/RVO indicator is a PC6 aggregate (% pre-1970 buildings), and Amsterdam
 * funderingsherstel is common but not captured anywhere we read. Demoting it
 * to a flag avoids miscapping the bulk of the Amsterdam Centrum market.
 *
 * Same shape applied to the "high" tier of flood (50–100 cm simulated depth,
 * remediable in principle) and seismic (5–10 km Loppersum band, where NCG
 * versterking status is not visible in any public per-building dataset).
 */
function computeRiskSignals(plot: PlotData): {
  cap: { reason: string; value: number } | null;
  flags: RiskFlag[];
} {
  const caps: { reason: string; value: number }[] = [];
  const flags: RiskFlag[] = [];

  // Flood risk (climate). very_high (>1 m simulated depth) caps; high becomes
  // a flag because property-level mitigation (raised floors, sump pumps,
  // internal flood barriers) exists but isn't visible in a national model.
  // Since 2026-09-14 the category comes from Rijkswaterstaat's flood-
  // PROBABILITY map (lib/climateSources), which publishes no depth — so "high"
  // is its once-per-10-years class, and nothing reaches very_high from it.
  const flood = plot.climateRisk?.floodRisk;
  if (flood === "very_high") {
    caps.push({ reason: "Very high flood risk", value: 55 });
  } else if (flood === "high") {
    flags.push({
      reason: "High flood risk",
      detail: plot.climateRisk?.floodDepthCm != null
        ? "Simulated flood depth in the 50–100 cm band under the national climate atlas. Per-plot mitigation (raised floors, internal barriers) isn't captured in the depth model."
        : "Rijkswaterstaat's flood map puts this location in its highest class: flooding from the sea, a lake or a major river about once every 10 years. Per-plot mitigation (raised floors, internal barriers) isn't captured in the national model.",
    });
  }

  // Soil contamination — two independent sources, both per-plot legal
  // registrations with statutory disclosure in sales. Strongest evidence
  // of all four risk categories; keep as cap.
  if (plot.climateRisk?.soilContamination === true) {
    caps.push({ reason: "Soil contamination (Bodemloket)", value: 50 });
  }
  const wkpb = plot.zoningDetail?.wkpb || [];
  if (wkpb.some(r => /verontreiniging|bodem/i.test(r.type))) {
    caps.push({ reason: "Soil contamination (WKPB)", value: 50 });
  }

  // Foundation risk (funderingsproblematiek) — flag-only.
  //
  // The KCAF/RVO dataset polygonises PC6 areas by share of pre-1970 buildings;
  // it tells us "your postcode contains a lot of old buildings", not "your
  // foundation is failing". We gate on this plot's bouwjaar to filter out the
  // false positives where the address is post-1970, but for the addresses
  // that remain we cannot see whether funderingsherstel has already been done.
  // In Amsterdam Centrum nearly the entire historic core triggers — capping
  // there would suppress the most active market segment on indirect evidence.
  const fr = plot.foundationRisk;
  const plotBouwjaar = plot.buildings?.find(b => b?.bouwjaar != null)?.bouwjaar ?? null;
  const ageSuggestsVulnerable = plotBouwjaar == null || plotBouwjaar < 1970;
  if (fr?.loaded && fr.inRiskZone === true && ageSuggestsVulnerable) {
    if (fr.riskLevel === "very_high") {
      flags.push({
        reason: "Very high foundation risk zone",
        detail: "≥80% of buildings in this PC6 are pre-1970 (KCAF/RVO indicator). The dataset cannot see whether this specific foundation has already had funderingsherstel — verify with a structural inspection.",
      });
    } else if (fr.riskLevel === "high") {
      flags.push({
        reason: "High foundation risk zone",
        detail: "60–80% of buildings in this PC6 are pre-1970 (KCAF/RVO indicator). The dataset cannot see whether this specific foundation has already had funderingsherstel — verify with a structural inspection.",
      });
    }
  }

  // Measured ground motion, when it is severe.
  //
  // A FLAG, not a cap. It is strong evidence — radar, this location, not a
  // postcode average — but "the ground here is dropping fast" is not the same
  // claim as "this building is damaged". A modern building on deep piles can
  // sit in a fast-subsiding polder and be entirely sound, and a surveyor is
  // the one who can tell the difference. So it is surfaced without moving the
  // number, which is what the amber flag means in the panel.
  //
  // The threshold is the worst 1% NATIONALLY, read from the distribution the
  // bake measured rather than a millimetre figure chosen here — the whole
  // country is sinking slightly, so any fixed rate is either everywhere or
  // nowhere.
  const subs = plot.subsidence;
  if (subs?.loaded && subs.covered && subs.mmPerYear != null && subs.national) {
    const pctile = nationalPercentile(subs.mmPerYear, subs.national);
    if (pctile <= 1) {
      const near = subs.distanceM != null && subs.distanceM > 0
        ? ` The nearest radar-stable measurement is ${subs.distanceM} m away, not on the plot itself.`
        : "";
      flags.push({
        reason: "Fast ground subsidence measured here",
        detail: `Copernicus EGMS radar measures ${subs.mmPerYear.toFixed(1)} mm/year of vertical ground movement at this location over ${subs.period ?? "the observation period"} — among the fastest 1% in the Netherlands. This measures motion, not damage, and says nothing about pile rot, which is driven by groundwater level rather than movement. Have a structural surveyor assess the foundation.${near}`,
      });
    }
  }

  // Seismic risk (Groningen gas field). very_high (≤5 km from Loppersum,
  // PGA ~0.20 g) caps — the property-value discount is documented in
  // actual sales and the hazard model is well-established at this distance.
  // High (5–10 km) becomes a flag — versterking work tracked by the NCG
  // (Nationaal Coördinator Groningen) is per-address and not visible in any
  // public dataset we read.
  const sr = plot.seismicRisk;
  if (sr?.loaded) {
    if (sr.riskLevel === "very_high") {
      caps.push({ reason: "Very high seismic risk (Groningen gas field)", value: 55 });
    } else if (sr.riskLevel === "high") {
      flags.push({
        reason: "High seismic risk (Groningen gas field)",
        detail: "Plot is within 5–10 km of the historic Loppersum epicentre cluster. NCG versterking status is per-address and not visible in public datasets — verify whether structural reinforcement has been applied.",
      });
    }
  }

  const cap = caps.length === 0
    ? null
    : caps.reduce((min, c) => (c.value < min.value ? c : min));
  return { cap, flags };
}

export function computePropertyScore(plot: PlotData, weights: ScoreWeights, cal?: ScoreCalibration): PropertyScore {
  const sub = {
    valuation: scoreValuation(plot, cal),
    building: scoreBuilding(plot, cal),
    location: scoreLocation(plot),
    neighborhood: scoreNeighborhood(plot),
    investment: scoreInvestment(plot),
  };

  // Normalize weights to sum=100
  const rawSum = weights.valuation + weights.building + weights.location + weights.neighborhood + weights.investment;
  const norm = rawSum > 0 ? 100 / rawSum : 1;

  // Weighted average, redistributing weight from zero-confidence sub-scores
  let totalWeight = 0;
  let weightedSum = 0;
  let totalConfidence = 0;

  const entries: [keyof typeof sub, number][] = [
    ["valuation", weights.valuation * norm],
    ["building", weights.building * norm],
    ["location", weights.location * norm],
    ["neighborhood", weights.neighborhood * norm],
    ["investment", weights.investment * norm],
  ];

  // Weight-aware composite confidence. Each sub-score's confidence contributes
  // in proportion to that sub-score's preset weight, so missing data matters
  // where the user is actually looking: on Investor (Investment weight 40) a
  // missing Investment sub-score drops composite confidence much more than on
  // Homeowner (Investment weight 10). Previous implementation divided by 5
  // regardless of preset, which confounded the two cases.
  for (const [key, w] of entries) {
    const s = sub[key];
    if (s.confidence > 0) {
      weightedSum += s.score * w;
      totalWeight += w;
    }
    totalConfidence += s.confidence * w; // w values are already normalized to sum=100
  }

  const unshrunk = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;
  const confidence = totalConfidence / 100;

  // Thresholded shrinkage toward the neutral midpoint (50). Above TRUST_FLOOR
  // (0.5) we trust the composite as computed — i.e. if at least half of the
  // weighted factors are present, don't distort the headline. Below the floor
  // we linearly pull toward 50, scaled by how far below the floor we are, so
  // a plot with 10% coverage still surfaces as a mid-50s number rather than
  // an extreme score we can't defend.
  //
  // Why this instead of sqrt(confidence)? sqrt still pulled 30%-coverage plots
  // 45% toward 50, which over-corrected typical Dutch properties where partial
  // data is legitimate and the remaining signal is still meaningful. Methodology
  // review (2026-04) flagged the sqrt prior as too strong and undefended.
  const TRUST_FLOOR = 0.5;
  const prior = 50;
  let rawComposite: number;
  if (confidence >= TRUST_FLOOR) {
    rawComposite = unshrunk;
  } else {
    const shrinkFactor = confidence / TRUST_FLOOR; // 0 at conf=0, 1 at conf=TRUST_FLOOR
    rawComposite = Math.round(unshrunk * shrinkFactor + prior * (1 - shrinkFactor));
  }

  // Detect exclusion (memorial / park / school / heritage / …). Computed AFTER
  // the composite so that `caveat`-tier plots still surface a meaningful number,
  // and so UI that wants internal diagnostics on `not_scored` plots has access
  // to the raw sub-scores. UI is responsible for respecting the tier.
  const exclusion = detectExclusion(plot) ?? undefined;

  // Apply risk cap (deal-breaker hard floor) and surface non-capping flags.
  // Flags ride alongside the cap when both are present — a plot can be capped
  // by flood AND flagged for foundation risk; the popover lists both.
  const { cap, flags } = computeRiskSignals(plot);
  const flagsOut = flags.length > 0 ? flags : undefined;
  if (cap && rawComposite > cap.value) {
    return { composite: cap.value, rawComposite, cap, flags: flagsOut, exclusion, confidence, sub };
  }
  return { composite: rawComposite, flags: flagsOut, exclusion, confidence, sub };
}
